import { calculateOrderItemTotals } from "./salesUtils";

// Pengaturan tampilan nota cetak (visual-only).
// Disimpan di localStorage, tidak ada data baru di backend.
// Hanya mengubah skala / ukuran kertas pada saat print.

const STORAGE_KEY = "receiptPrintConfig";

const DEFAULTS = {
  orientation: "horizontal", // 'horizontal' | 'thermal'
  thermalWidth: "58mm", // '58mm' | '80mm'
  printLogo: true,
  orderReceiptEnabled: false,
  splitOrderEnabled: false,
  autoPrintEnabled: false,
  autoOpenCashDrawer: false,
  splitOrderReceipts: [
    { id: "checker", name: "Nota Checker", categories: "ALL", printerMac: "", printerName: "" },
    { id: "dapur", name: "Nota Dapur", categories: [], printerMac: "", printerName: "" },
    { id: "bar", name: "Nota Bar", categories: [], printerMac: "", printerName: "" },
  ],
  // === Label Pesanan (Kuliner Only) ===
  orderLabelEnabled: false,
  autoPrintLabelEnabled: false,
  labelPrinterMac: "",
  labelPrinterName: "",
  labelPrinterWidth: "58mm",
  labelCategories: "ALL", // 'ALL' | string[] of category IDs
  labelCategoryNames: [], // string[] for category names preview
};

export const THERMAL_WIDTHS = ["58mm", "80mm"];
export const ORIENTATIONS = [
  { value: "horizontal", label: "Horizontal (A5 Landscape)" },
  { value: "thermal", label: "Vertical (Printer Thermal)" },
];

const PAYMENT_METHOD_LABELS = {
  CASH: "Tunai",
  TUNAI: "Tunai",
  TRANSFER: "Transfer",
  BANK_TRANSFER: "Transfer",
  QRIS: "QRIS",
  MIXED: "Campuran",
  CAMPURAN: "Campuran",
};

export function getReceiptPaymentMethodLabel(totals = {}) {
  const payments = Array.isArray(totals.payments) ? totals.payments : [];
  const methods = [
    ...new Set(
      payments
        .map((payment) =>
          String(payment?.payment_method || payment?.method || "")
            .trim()
            .toUpperCase(),
        )
        .filter(Boolean),
    ),
  ];

  if (methods.includes("MIXED") || methods.length > 1) return "Campuran";
  if (methods.length === 1) {
    return PAYMENT_METHOD_LABELS[methods[0]] || methods[0];
  }

  const suppliedMethod = String(
    totals.paymentMethodLabel ||
      totals.paymentMethod ||
      totals.payment_method ||
      "Tunai",
  ).trim();
  const suppliedKey = suppliedMethod.toUpperCase();

  return PAYMENT_METHOD_LABELS[suppliedKey] || suppliedMethod || "Tunai";
}

export function getReceiptConfig() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return { ...DEFAULTS };
    const parsed = JSON.parse(raw);
    return {
      orientation: parsed?.orientation === "thermal" ? "thermal" : "horizontal",
      thermalWidth: THERMAL_WIDTHS.includes(parsed?.thermalWidth)
        ? parsed.thermalWidth
        : DEFAULTS.thermalWidth,
      printLogo: parsed?.printLogo !== undefined ? !!parsed.printLogo : DEFAULTS.printLogo,
      orderReceiptEnabled: parsed?.orderReceiptEnabled !== undefined ? !!parsed.orderReceiptEnabled : DEFAULTS.orderReceiptEnabled,
      splitOrderEnabled: parsed?.splitOrderEnabled !== undefined ? !!parsed.splitOrderEnabled : DEFAULTS.splitOrderEnabled,
      autoPrintEnabled: parsed?.autoPrintEnabled !== undefined ? !!parsed.autoPrintEnabled : DEFAULTS.autoPrintEnabled,
      autoOpenCashDrawer: parsed?.autoOpenCashDrawer !== undefined ? !!parsed.autoOpenCashDrawer : DEFAULTS.autoOpenCashDrawer,
      splitOrderReceipts: parsed?.splitOrderReceipts || DEFAULTS.splitOrderReceipts,
      // Label Pesanan
      orderLabelEnabled: parsed?.orderLabelEnabled !== undefined ? !!parsed.orderLabelEnabled : DEFAULTS.orderLabelEnabled,
      autoPrintLabelEnabled: parsed?.autoPrintLabelEnabled !== undefined ? !!parsed.autoPrintLabelEnabled : DEFAULTS.autoPrintLabelEnabled,
      labelPrinterMac: parsed?.labelPrinterMac || DEFAULTS.labelPrinterMac,
      labelPrinterName: parsed?.labelPrinterName || DEFAULTS.labelPrinterName,
      labelPrinterWidth: THERMAL_WIDTHS.includes(parsed?.labelPrinterWidth) ? parsed.labelPrinterWidth : DEFAULTS.labelPrinterWidth,
      labelCategories: parsed?.labelCategories !== undefined ? parsed.labelCategories : DEFAULTS.labelCategories,
      labelCategoryNames: Array.isArray(parsed?.labelCategoryNames) ? parsed.labelCategoryNames : DEFAULTS.labelCategoryNames,
    };
  } catch {
    return { ...DEFAULTS };
  }
}

/** Shortcut: ambil hanya config label printer */
export function getLabelPrinterConfig() {
  const cfg = getReceiptConfig();
  return {
    enabled: cfg.orderLabelEnabled,
    autoPrint: cfg.autoPrintLabelEnabled,
    printerMac: cfg.labelPrinterMac,
    printerName: cfg.labelPrinterName,
    printerWidth: cfg.labelPrinterWidth,
    labelCategories: cfg.labelCategories || "ALL",
    labelCategoryNames: cfg.labelCategoryNames || [],
  };
}

/**
 * Filter item untuk cetak label pesanan berdasarkan mapping kategori
 * @param {Array} items - array item transaksi
 * @param {string|Array} labelCategories - "ALL" atau array category_id
 * @returns {Array} item yang lolos filter
 */
export function filterLabelItems(items = [], labelCategories = "ALL", labelCategoryNames = []) {
  if (!items || !Array.isArray(items) || items.length === 0) return [];
  if (!labelCategories || labelCategories === "ALL") {
    return items;
  }
  if (Array.isArray(labelCategories)) {
    if (labelCategories.length === 0) return [];
    const catNamesArr = Array.isArray(labelCategoryNames) ? labelCategoryNames : [];

    return items.filter((item) => {
      const itemCatId = String(
        item.category_id ||
        item.category ||
        item.product?.category_id ||
        item.product_category_id ||
        ""
      ).trim().toLowerCase();
      const itemCatName = String(
        item.category_name ||
        item.category ||
        item.product?.category_name ||
        ""
      ).trim().toLowerCase();
      if (!itemCatId && !itemCatName) return false;

      const matchId = labelCategories.some((cat) => {
        const catStr = String(cat || "").trim().toLowerCase();
        return (itemCatId && itemCatId === catStr) || (itemCatName && itemCatName === catStr);
      });
      if (matchId) return true;

      const matchName = catNamesArr.some((cname) => {
        const nameStr = String(cname || "").trim().toLowerCase();
        return itemCatName && itemCatName === nameStr;
      });
      return matchName;
    });
  }
  return items;
}

export function setReceiptConfig(next) {
  const merged = { ...getReceiptConfig(), ...next };
  localStorage.setItem(STORAGE_KEY, JSON.stringify(merged));
  return merged;
}

// CSS untuk halaman nota. Layout thermal (vertical) memakai struktur strip
// monospace; layout horizontal (A5 Landscape) memakai layout invoice 2 kolom.
export function getReceiptPrintCss() {
  const cfg = getReceiptConfig();
  const isThermal = cfg.orientation === "thermal";
  const thermalWidth = cfg.thermalWidth || "80mm";
  const baseFontSize = thermalWidth === '58mm' ? '10px' : '11px';

  if (isThermal) {
    const paper = cfg.thermalWidth === '58mm' ? '58mm' : '80mm';
    const printableWidth = cfg.thermalWidth === '58mm' ? '48mm' : '72mm';
    const wrapWidth = cfg.thermalWidth === '58mm' ? '185px' : '275px';
    // Penting: @page hanya satu nilai berarti width=height (square paper).
    // Kita perlu width thermal + tinggi yang cukup agar nota tidak terpotong
    // jadi beberapa halaman saat di-Save as PDF.
    return `
      @page { size: ${paper} auto; margin: 0; }
      html, body { margin: 0; padding: 0; }
      body {
        font-family: 'Menlo', 'Consolas', 'Courier New', monospace;
        font-size: ${baseFontSize};
        color: #000;
        padding: 8px;
        background: #fff;
        -webkit-print-color-adjust: exact;
        color-adjust: exact;
      }
      .wrap {
        width: ${wrapWidth};
        max-width: 100%;
        margin: 0 auto;
        background: #fff;
        border: 1px solid #e5e5e5;
        padding: 3mm;
        box-sizing: border-box;
      }
      p { margin: 0; padding: 0; }
      .center { text-align: center; }
      .right { text-align: right; }
      .muted { color: #555; }
      .store-logo {
        display: block;
        margin: 0 auto 4px auto;
        max-width: 60px;
        max-height: 60px;
        object-fit: contain;
      }
      .store-name {
        text-align: center;
        font-size: 15px;
        font-weight: 700;
        margin: 0;
        letter-spacing: 0.3px;
      }
      .store-sub {
        text-align: center;
        font-size: 10px;
        color: #333;
        margin: 0;
      }
      .div-dash { display: block; width: 100%; overflow: hidden; white-space: nowrap; color: #555; padding: 4px 0; margin: 0; box-sizing: border-box; letter-spacing: 1px; }
      
      .meta-row { display: flex; justify-content: space-between; gap: 8px; font-size: ${baseFontSize}; line-height: 1.45; margin: 0; }
      .meta-cashier { margin: 0; padding: 0; font-size: ${baseFontSize}; }
      .item-block { margin: 0; padding: 2px 0; font-size: ${baseFontSize}; line-height: 1.45; }
      .item-block .item-name { font-weight: 600; word-break: break-word; overflow-wrap: anywhere; }
      .item-block .item-line { display: flex; justify-content: space-between; gap: 6px; padding-left: 14px; font-size: ${baseFontSize}; }
      .item-block .item-line .qty { white-space: nowrap; }
      .item-block .item-line .total { white-space: nowrap; text-align: right; }
      .item-adjustments { margin: 1px 0 2px; }
      .item-adjustments .item-line { padding-left: 0; }
      .item-adjustments .adjustment-value {
        min-width: 78px;
        white-space: nowrap;
        text-align: right;
        font-variant-numeric: tabular-nums;
      }
      .item-adjustments .item-total-row {
        border-top: 1px dotted #777;
        margin-top: 1px;
        padding-top: 1px;
        font-weight: 700;
      }
      .summary { width: 100%; border-collapse: collapse; margin: 0; padding: 0; }
      .summary td { padding: 2px 0; font-size: ${baseFontSize}; }
      .summary td.label { text-align: left; }
      .summary td.val { text-align: right; }
      .summary tr.strong td { font-weight: 700; }
      .footer { text-align: center; margin: 0; font-size: 10px; color: #333; }
      .footer p { margin: 0; padding: 2px 0; }
      .footer .note { margin: 0; padding: 0 0 2px 0; font-weight: 600; }
      .actions, .no-print { margin-top: 14px; display: flex; justify-content: center; align-items: center; gap: 10px; }
      .btn { border: none; background: #111; color: #fff; border-radius: 6px; padding: 8px 14px; cursor: pointer; font-family: inherit; }
      .btn-close { background: #475569; color: #fff; font-weight: bold; }
      @page {
        size: ${paper} auto;
        margin: 0;
      }
      @media print {
        html, body {
          width: ${paper} !important;
          max-width: ${paper} !important;
          min-width: ${paper} !important;
          margin: 0 !important;
          padding: 0 !important;
          background: #fff;
          -webkit-print-color-adjust: exact;
          print-color-adjust: exact;
        }
        .wrap {
          width: ${printableWidth} !important;
          max-width: ${printableWidth} !important;
          min-width: 0 !important;
          min-height: auto;
          border: none !important;
          padding: 2mm 0 !important;
          margin: 0 auto !important;
          box-shadow: none !important;
          box-sizing: border-box !important;
        }
        .wrap * {
          max-width: 100% !important;
          box-sizing: border-box !important;
        }
        .meta-row,
        .item-block .item-line {
          min-width: 0 !important;
        }
        .meta-row > *,
        .item-block .item-line > * {
          min-width: 0 !important;
        }
        .actions, .no-print { display: none !important; }
      }
    `;
  }

  // === Horizontal (A5 Landscape) — invoice-style 2 column ===
  // Dirancang muat 1 halaman A5 landscape (210x148mm) presisi di tengah kertas.
  return `
    @page {
      size: A5 landscape;
      margin: 0;
    }
    html, body {
      margin: 0;
      padding: 0;
      background: #f8fafc;
      -webkit-print-color-adjust: exact;
      print-color-adjust: exact;
    }
    body {
      font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif;
      font-size: 10px;
      color: #0f172a;
      padding: 10px 0;
      -webkit-font-smoothing: antialiased;
      -moz-osx-font-smoothing: grayscale;
      text-rendering: geometricPrecision;
    }
    .wrap {
      width: 196mm;
      max-width: 196mm;
      margin: 0 auto;
      background: #fff;
      min-height: auto;
      border: none;
      padding: 5mm 6mm;
      box-sizing: border-box;
      box-shadow: 0 1px 3px rgba(0,0,0,0.05);
    }
    /* HEADER — store (left) + customer/meta (right) */
    .h-header {
      display: grid;
      grid-template-columns: 1fr 1fr;
      gap: 12px;
      margin-bottom: 6px;
      align-items: start;
    }
    .h-store { display: flex; gap: 10px; align-items: flex-start; }
    .h-store .logo {
      width: 44px; height: 44px; flex-shrink: 0;
      border-radius: 50%; object-fit: cover;
      border: 1px solid #e5e5e5;
    }
    .h-store .logo-fallback {
      width: 44px; height: 44px; flex-shrink: 0;
      border-radius: 50%; background: #f3f4f6;
      display: flex; align-items: center; justify-content: center;
      font-weight: 700; font-size: 14px; color: #6b7280;
    }
    .h-store-info h1 {
      font-size: 14px; font-weight: 800; margin: 0 0 2px 0;
      letter-spacing: 0.3px; color: #0f172a;
    }
    .h-store-info p { font-size: 8.5px; color: #475569; margin: 1px 0; line-height: 1.35; max-width: 240px; }
    /* Customer / meta info — right column, uses flex rows */
    .h-customer { font-size: 9.5px; line-height: 1.5; }
    .h-customer .crow {
      display: flex;
      justify-content: flex-end;
      gap: 2px;
      margin-bottom: 2px;
    }
    .h-customer .crow .clabel {
      color: #475569;
      text-align: left;
      width: 115px;
      white-space: nowrap;
      flex-shrink: 0;
    }
    .h-customer .crow .csep {
      color: #475569;
      width: 12px;
      text-align: center;
      flex-shrink: 0;
    }
    .h-customer .crow .cval {
      text-align: left;
      color: #0f172a;
      width: 135px;
      flex-shrink: 0;
      word-break: break-word;
    }
    .h-customer .crow .cval.bold { font-weight: 700; }
    .h-divider {
      border: 0;
      border-top: 1px solid #000000;
      margin: 6px 0;
    }
    /* ITEMS table */
    .h-items {
      width: 100%;
      border-collapse: collapse;
      margin-top: 4px;
      font-size: 10px;
      border: 1px solid #000000;
      table-layout: fixed;
    }
    .h-items col.col-no { width: 32px; }
    .h-items col.col-name { width: auto; }
    .h-items col.col-qty { width: 68px; }
    .h-items col.col-price { width: 95px; }
    .h-items col.col-total { width: 100px; }
    .h-items th {
      background: #f1f5f9;
      font-weight: 700;
      padding: 5px 6px;
      text-align: center;
      border: 1px solid #000000;
      color: #000000;
      font-size: 10px;
      box-sizing: border-box;
    }
    .h-items th.th-no, .h-items td.no { width: 32px; max-width: 32px; text-align: center; font-weight: 600; border: 1px solid #000000; }
    .h-items th.th-name, .h-items td.name { text-align: left; font-weight: 600; border: 1px solid #000000; }
    .h-items th.th-qty, .h-items td.qty { width: 68px; max-width: 68px; text-align: center; font-weight: 600; border: 1px solid #000000; }
    .h-items th.th-price, .h-items td.price { width: 95px; max-width: 95px; text-align: right; border: 1px solid #000000; white-space: nowrap; }
    .h-items th.th-total, .h-items td.total { width: 100px; max-width: 100px; text-align: right; font-weight: 700; color: #000000; border: 1px solid #000000; white-space: nowrap; }
    .h-items td {
      padding: 4px 6px;
      border: 1px solid #000000;
      vertical-align: middle;
      color: #000000;
      overflow: hidden;
      text-overflow: ellipsis;
      box-sizing: border-box;
    }
    /* FOOTER */
    .h-footer {
      display: grid;
      grid-template-columns: 1fr 1fr;
      gap: 12px;
      margin-top: 8px;
      align-items: start;
    }
    .h-footer .left { font-size: 10px; }
    .h-footer .left .signature { font-weight: 700; margin: 0 0 4px 0; color: #000000; }
    .h-footer .left .thanks { font-size: 9px; color: #4b5563; font-style: italic; border-top: 1px dashed #cbd5e1; padding-top: 4px; margin-top: 4px; }
    .h-footer .right { font-size: 10px; }
    .h-footer .right .sum-row {
      display: flex;
      justify-content: flex-end;
      gap: 2px;
      padding: 2px 0;
      border-bottom: 1px dashed #cbd5e1;
    }
    .h-footer .right .sum-row.strong { font-weight: 700; border-bottom: 1px solid #000000; }
    .h-footer .right .sum-row .label { color: #4b5563; width: 125px; text-align: left; }
    .h-footer .right .sum-row .colon { color: #4b5563; width: 12px; text-align: center; }
    .h-footer .right .sum-row .value { text-align: right; color: #111; font-weight: 600; width: 105px; white-space: nowrap; }
    .h-footer .right .sum-row.strong .value { font-weight: 700; color: #0f172a; }
    .h-footer .right .sum-row.status .value { font-weight: 700; color: #059669; }
    .h-poweredby {
      text-align: center;
      margin-top: 8px;
      font-size: 8px;
      color: #9ca3af;
    }
    /* Cegah element pecah antar halaman saat dicetak */
    .h-items tr { page-break-inside: avoid; break-inside: avoid; }
    .h-footer { page-break-inside: avoid; break-inside: avoid; }
    .actions, .no-print { margin-top: 14px; display: flex; justify-content: center; align-items: center; gap: 10px; }
    .btn { border: none; background: #111; color: #fff; border-radius: 6px; padding: 8px 14px; cursor: pointer; font-family: inherit; }
    .btn-close { background: #475569; color: #fff; font-weight: bold; }
    .btn-print { background: #0d9488; color: #fff; font-weight: bold; }
    @media print {
      * {
        -webkit-print-color-adjust: exact !important;
        print-color-adjust: exact !important;
        text-rendering: geometricPrecision !important;
      }
      @page {
        size: A5 landscape;
        margin: 0;
      }
      html, body {
        width: 210mm !important;
        max-width: 210mm !important;
        min-width: 210mm !important;
        margin: 0 auto !important;
        padding: 0 !important;
        background: #ffffff !important;
      }
      body {
        font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif !important;
        color: #000000 !important;
        -webkit-font-smoothing: antialiased !important;
      }
      .wrap {
        width: 196mm !important;
        max-width: 196mm !important;
        min-width: 196mm !important;
        margin: 3mm auto 0 auto !important;
        padding: 5mm 6mm !important;
        border: none !important;
        box-shadow: none !important;
        box-sizing: border-box !important;
        page-break-inside: avoid !important;
        break-inside: avoid !important;
      }
      .actions, .no-print { display: none !important; }
      .h-store-info h1,
      .h-store-info p,
      .h-customer .crow .clabel,
      .h-customer .crow .csep,
      .h-customer .crow .cval,
      .h-footer .left .signature,
      .h-footer .left .thanks,
      .h-footer .right .sum-row .label,
      .h-footer .right .sum-row .colon,
      .h-footer .right .sum-row .value,
      .h-poweredby {
        color: #000000 !important;
      }
      .h-items {
        width: 100% !important;
        border: 1px solid #000000 !important;
        table-layout: fixed !important;
      }
      .h-items col.col-no { width: 32px !important; }
      .h-items col.col-name { width: auto !important; }
      .h-items col.col-qty { width: 68px !important; }
      .h-items col.col-price { width: 95px !important; }
      .h-items col.col-total { width: 100px !important; }
      .h-items th {
        border: 1px solid #000000 !important;
        background: #f1f5f9 !important;
        color: #000000 !important;
        font-weight: 700 !important;
      }
      .h-items th.th-no, .h-items td.no {
        width: 32px !important;
        max-width: 32px !important;
        text-align: center !important;
        border: 1px solid #000000 !important;
      }
      .h-items th.th-name, .h-items td.name {
        text-align: left !important;
        border: 1px solid #000000 !important;
      }
      .h-items th.th-qty, .h-items td.qty {
        width: 68px !important;
        max-width: 68px !important;
        text-align: center !important;
        border: 1px solid #000000 !important;
      }
      .h-items th.th-price, .h-items td.price {
        width: 95px !important;
        max-width: 95px !important;
        text-align: right !important;
        border: 1px solid #000000 !important;
      }
      .h-items th.th-total, .h-items td.total {
        width: 100px !important;
        max-width: 100px !important;
        text-align: right !important;
        border: 1px solid #000000 !important;
      }
      .h-items td {
        border: 1px solid #000000 !important;
        color: #000000 !important;
      }
      .h-divider {
        border-top: 1px solid #000000 !important;
      }
      .h-footer .right .sum-row {
        border-bottom: 1px dashed #000000 !important;
      }
      .h-footer .right .sum-row.strong {
        border-bottom: 1px solid #000000 !important;
      }
    }
  `;
}

const escapeHtml = (value = "") =>
  String(value ?? "")
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#039;");

const formatNumber = (n) => {
  const num = Number(n) || 0;
  return num.toLocaleString("id-ID");
};

export function formatSalesTypeBracket(st) {
  if (!st) return "";
  if (typeof st === "string") {
    const s = st.trim();
    if (!s || ["INTERNAL", "REGULAR", "REGULER", "NORMAL", "-"].includes(s.toUpperCase())) {
      return "";
    }
    return s;
  }

  const name = String(st.name || st.slug || "").trim();
  if (!name || ["INTERNAL", "REGULAR", "REGULER", "NORMAL", "-"].includes(name.toUpperCase())) {
    return "";
  }

  const satuan = String(st.satuan || "").trim();
  const nilai = Number(st.nilai) || 0;

  // Jika satuan adalah persen (%)
  if (satuan === "%" || satuan.toLowerCase() === "persen") {
    if (name.includes("%")) return name;
    if (nilai > 0) return `${name} ${nilai}%`;
    return name;
  }

  // Jika satuan adalah nominal / Rp
  if (satuan.toUpperCase() === "RP" || satuan.toLowerCase() === "nominal" || satuan) {
    if (/rp/i.test(name)) return name;
    if (nilai > 0) return `${name} Rp ${formatNumber(nilai)}`;
    return name;
  }

  return name;
}

export function getReceiptAdjustmentLabels(data) {
  const totals = data?.totals || {};
  const meta = data?.meta || {};

  const salesTypeObj = meta.salesTypeObj || meta.orderType || (typeof meta.sellerType === "object" ? meta.sellerType : null);
  let discountLabel = totals.discountLabel || "Diskon";
  let additionalCostsLabel = totals.additionalCostsLabel || "Tambahan";

  const discountVal = Number(totals.discount ?? totals.discountTotal ?? 0);
  const additionalVal = Number(totals.additionalCosts ?? totals.additional_costs ?? totals.additional ?? 0);

  // Cek untuk Diskon
  if (discountVal > 0 && !totals.discountLabel) {
    if (salesTypeObj && salesTypeObj.jenis === "Diskon") {
      const formatted = formatSalesTypeBracket(salesTypeObj);
      if (formatted) {
        discountLabel = `Diskon (${formatted})`;
      }
    }
  }

  // Cek untuk Tambahan (Markup / Biaya Tambahan)
  if (additionalVal > 0 && !totals.additionalCostsLabel) {
    if (salesTypeObj && salesTypeObj.jenis === "Markup") {
      const formatted = formatSalesTypeBracket(salesTypeObj);
      if (formatted) {
        additionalCostsLabel = `Tambahan (${formatted})`;
      }
    }
  }

  return { discountLabel, additionalCostsLabel };
}

export const STANDARD_PHYSICAL_UNITS = [
  "pcs", "pc", "liter", "ltr", "box", "dus", "kg", "gram", "gr", "meter", "m",
  "sak", "kaleng", "botol", "unit", "pack", "paket", "lembar", "roll", "set",
  "buah", "btl", "can", "drum", "pail", "btg", "batang", "lusin", "koli", "kal",
  "karton", "galon", "sachet", "tube", "porsi", "ikat", "butir", "pasang"
];

/**
 * Deteksi unit armada kendaraan transaksi (khas Legiun, misal TRAGA 001, CAT 005, CANTER 002).
 * Memeriksa meta, data, dan seluruh items untuk menemukan unit kendaraan armada secara akurat.
 */
export function detectVehicleUnit(meta = {}, items = [], data = {}) {
  if (meta?.unitName && typeof meta.unitName === "string" && meta.unitName.trim()) {
    return meta.unitName.trim();
  }
  if (meta?.unit) {
    if (typeof meta.unit === "string" && meta.unit.trim()) return meta.unit.trim();
    if (typeof meta.unit === "object" && meta.unit.name) return String(meta.unit.name).trim();
  }
  if (meta?.asset_unit_name && typeof meta.asset_unit_name === "string" && meta.asset_unit_name.trim()) {
    return meta.asset_unit_name.trim();
  }
  if (data?.unitName && typeof data.unitName === "string" && data.unitName.trim()) {
    return data.unitName.trim();
  }
  if (data?.unit) {
    if (typeof data.unit === "string" && data.unit.trim()) return data.unit.trim();
    if (typeof data.unit === "object" && data.unit.name) return String(data.unit.name).trim();
  }
  for (const it of (items || [])) {
    const assetUnit = it?.asset_unit_name || (typeof it?.asset_unit === "object" ? it?.asset_unit?.name : it?.asset_unit);
    if (assetUnit && typeof assetUnit === "string" && assetUnit.trim()) {
      return assetUnit.trim();
    }
    const rawCandidate = it?.unit_name || (typeof it?.unit === "object" ? it?.unit?.name : (typeof it?.unit === "string" ? it.unit : ""));
    if (rawCandidate && typeof rawCandidate === "string") {
      const trimmed = rawCandidate.trim();
      // Bila bukan satuan kemasan fisik standar, ini adalah nama unit armada kendaraan
      if (trimmed && !STANDARD_PHYSICAL_UNITS.includes(trimmed.toLowerCase())) {
        return trimmed;
      }
    }
  }
  return "";
}

/**
 * Mengambil satuan fisik produk (kemasan/barang) yang valid untuk kolom Jumlah nota.
 * DILARANG KERAS mengembalikan nama unit kendaraan armada (misal TRAGA 001).
 */
export function getPhysicalProductUnit(it, detectedUnitName = "", isKuliner = false) {
  const directPhys =
    it?.satuan ||
    it?.product_unit_name ||
    it?.selling_unit_name ||
    (typeof it?.selling_unit === "object" ? it?.selling_unit?.name : it?.selling_unit) ||
    (typeof it?.product?.unit === "string" ? it?.product?.unit : (it?.product?.unit?.name || it?.product?.unit_name)) ||
    "";
  const directPhysStr = typeof directPhys === "object" ? (directPhys?.name || "") : String(directPhys || "").trim();

  const rawUnit = it?.unit_name || (typeof it?.unit === "object" ? it?.unit?.name : it?.unit);
  const rawUnitStr = typeof rawUnit === "object" ? (rawUnit?.name || "") : String(rawUnit || "").trim();

  const isCandidateVehicle = (str) => {
    if (!str) return false;
    const s = str.toLowerCase();
    if (detectedUnitName && s === detectedUnitName.toLowerCase()) return true;
    return !STANDARD_PHYSICAL_UNITS.includes(s);
  };

  // Jika rawUnit valid dan bukan unit armada
  if (rawUnitStr && !isCandidateVehicle(rawUnitStr)) {
    return rawUnitStr;
  }

  // Jika directPhysStr valid dan bukan unit armada
  if (directPhysStr && !isCandidateVehicle(directPhysStr)) {
    return directPhysStr;
  }

  // Default satuan barang jika tidak ada satuan fisik eksplisit
  return !isKuliner ? "Pcs" : "";
}


// Ambil nama kasir untuk nota:
// Prioritas UTAMA: Pengguna yang sedang login saat ini (full_name / name dari user_data)
// Fallback: Nama kasir dari shift aktif jika user_data kosong
const getLoggedInUserName = () => {
  try {
    const raw = localStorage.getItem("user_data");
    const parsed = raw ? JSON.parse(raw) : null;
    
    const loggedInName = parsed?.full_name || parsed?.name || localStorage.getItem("username") || parsed?.email;
    if (loggedInName && loggedInName.trim()) return loggedInName.trim();

    const shiftCashier = localStorage.getItem("activeShiftCashierName");
    if (shiftCashier && shiftCashier.trim()) return shiftCashier.trim();

    return "";
  } catch {
    return localStorage.getItem("username") || "";
  }
};

function groupItems(items) {
  const grouped = [];
  (items ?? []).forEach((item) => {
    if (item.product_addon_id) {
      let parent = null;
      for (let i = grouped.length - 1; i >= 0; i--) {
        if (grouped[i].product_id === item.product_id && !grouped[i].product_addon_id) {
          parent = grouped[i];
          break;
        }
      }
      if (parent) {
        if (!parent.addons) parent.addons = [];
        
        let cleanName = item.name || item.item_name || "";
        const parentName = parent.name || "";
        if (parentName && cleanName.toLowerCase().startsWith(parentName.toLowerCase() + " - ")) {
          cleanName = cleanName.substring(parentName.length + 3);
        } else {
          const parts = cleanName.split(" - ");
          if (parts.length > 1) {
            cleanName = parts[parts.length - 1];
          }
        }

        parent.addons.push({
          id: item.id,
          name: cleanName,
          qty: Number(item.qty || item.quantity || 0),
          price: Number(item.price || item.unit_price || 0),
          total: Number(item.total || item.total_price || 0),
          unit_name: item.unit || item.unit_name || ""
        });
        return;
      }
    }
    const itemUnit = item.unit_name || item.unit?.name || item.asset_unit_name || item.selling_unit_name || item.selling_unit?.name || (typeof item.unit === "string" ? item.unit : "") || (typeof item.selling_unit === "string" ? item.selling_unit : "") || item.product?.unit || item.product?.unit_name || item.product?.selling_unit || item.product?.selling_unit_name || item.satuan || "";
    const copy = {
      ...item,
      id: item.id,
      name: item.name || item.item_name || "-",
      qty: Number(item.qty || item.quantity || 0),
      price: Number(item.price || item.unit_price || 0),
      total: Number(item.total || item.total_price || 0),
      unit: itemUnit || item.unit || "",
      unit_name: itemUnit || item.unit_name || "",
      variant_name: item.variant_name || item.variant || "",
      addons: []
    };
    grouped.push(copy);
  });
  return grouped;
}

/**
 * Khusus FE Legiun: Nomor transaksi ditampilkan lengkap (tidak disingkat).
 */
export function formatSimplifiedTxNum(txNum) {
  if (!txNum || txNum === "-") return "-";
  return String(txNum).trim();
}

export function formatDateIndo(raw) {
  if (!raw || raw === "-") return "-";
  const monthNames = [
    "Jan", "Feb", "Mar", "Apr", "Mei", "Jun",
    "Jul", "Agu", "Sep", "Okt", "Nov", "Des"
  ];
  const str = String(raw).trim();

  // Match 24/06/2026 or 24/06/26 or 24-06-26 or 2026-06-24
  const dateMatch = str.match(/^(\d{1,4})[\/\-](\d{1,2})[\/\-](\d{1,4})/);
  if (dateMatch) {
    let p1 = Number(dateMatch[1]);
    let p2 = Number(dateMatch[2]);
    let p3 = Number(dateMatch[3]);

    let day, monthIdx, year;
    if (p1 > 31) {
      year = p1 < 100 ? 2000 + p1 : p1;
      monthIdx = Math.max(0, Math.min(11, p2 - 1));
      day = String(p3).padStart(2, "0");
    } else {
      day = String(p1).padStart(2, "0");
      monthIdx = Math.max(0, Math.min(11, p2 - 1));
      year = p3 < 100 ? 2000 + p3 : p3;
    }
    return `${day} ${monthNames[monthIdx]} ${year}`;
  }

  // Match "24 Jun 2026" or "24 Ags 26"
  const textMatch = str.match(/^(\d{1,2})\s+([A-Za-z]+)\s+(\d{2,4})/);
  if (textMatch) {
    const day = textMatch[1].padStart(2, "0");
    const mStr = textMatch[2].toLowerCase();
    let y = Number(textMatch[3]);
    const year = y < 100 ? 2000 + y : y;

    const monthMap = {
      jan: 0, feb: 1, mar: 2, apr: 3, mei: 4, jun: 5,
      jul: 6, agu: 7, ags: 7, sep: 8, okt: 9, nov: 10, des: 11,
      january: 0, february: 1, march: 2, april: 3, may: 4, june: 5,
      july: 6, august: 7, september: 8, october: 9, november: 10, december: 11
    };
    const mIdx = monthMap[mStr] !== undefined ? monthMap[mStr] : 0;
    return `${day} ${monthNames[mIdx]} ${year}`;
  }

  const d = new Date(raw);
  if (!isNaN(d.getTime())) {
    const day = String(d.getDate()).padStart(2, "0");
    const month = monthNames[d.getMonth()] || "Jan";
    const year = d.getFullYear();
    return `${day} ${month} ${year}`;
  }
  return str;
}

export function formatOrderDateTime(raw) {
  const monthNames = [
    "Jan", "Feb", "Mar", "Apr", "Mei", "Jun",
    "Jul", "Agu", "Sep", "Okt", "Nov", "Des"
  ];

  if (!raw) {
    const now = new Date();
    const day = now.getDate();
    const month = monthNames[now.getMonth()];
    const year = now.getFullYear();
    const hours = String(now.getHours()).padStart(2, "0");
    const mins = String(now.getMinutes()).padStart(2, "0");
    return `${day} ${month} ${year}, ${hours}.${mins}`;
  }

  if (raw instanceof Date && !isNaN(raw.getTime())) {
    const day = raw.getDate();
    const month = monthNames[raw.getMonth()];
    const year = raw.getFullYear();
    const hours = String(raw.getHours()).padStart(2, "0");
    const mins = String(raw.getMinutes()).padStart(2, "0");
    return `${day} ${month} ${year}, ${hours}.${mins}`;
  }

  const str = String(raw).trim();

  // If already like "25 Jan 2026, 15.55" or "25 Jan 2026, 15:55"
  if (/^\d{1,2}\s+[A-Za-z]{3,}\s+\d{2,4},\s*\d{1,2}[.:]\d{2}/.test(str)) {
    return str.replace(/:/g, ".");
  }

  // If contains comma
  if (str.includes(",")) {
    const [dPart, tPart] = str.split(",").map((s) => s.trim());
    const formattedDate = formatDateIndo(dPart);
    let cleanTime = tPart ? tPart.replace(/WIB/i, "").trim() : "";
    const timeMatch = cleanTime.match(/(\d{1,2})[:.](\d{2})/);
    if (timeMatch) {
      cleanTime = `${timeMatch[1].padStart(2, "0")}.${timeMatch[2]}`;
    }
    return `${formattedDate}${cleanTime ? `, ${cleanTime}` : ""}`;
  }

  const parsed = new Date(str);
  if (!isNaN(parsed.getTime())) {
    const day = parsed.getDate();
    const month = monthNames[parsed.getMonth()];
    const year = parsed.getFullYear();
    const hours = String(parsed.getHours()).padStart(2, "0");
    const mins = String(parsed.getMinutes()).padStart(2, "0");
    return `${day} ${month} ${year}, ${hours}.${mins}`;
  }

  return str;
}

export function buildReceiptHtml(data) {
  const {
    title = "Nota Transaksi",
    storeName = "Toko",
    storeAddress = "",
    storePhone = "",
    storeFooterNote = "Terima Kasih Telah Menjadi Pelanggan Kami",
    storeLogoUrl = "",
    storeTagline = "",
    meta = {},
    items = [],
    totals = {},
  } = data || {};

  // Format tanggal (24 Jun 2026) & jam terpisah dari transactionDateTime jika ada.
  const formatDateIndo = (raw) => {
    if (!raw || raw === "-") return "-";
    const monthNames = [
      "Jan", "Feb", "Mar", "Apr", "Mei", "Jun",
      "Jul", "Agu", "Sep", "Okt", "Nov", "Des"
    ];
    const str = String(raw).trim();

    // Match 24/06/2026 or 24/06/26 or 24-06-26 or 2026-06-24
    const dateMatch = str.match(/^(\d{1,4})[\/\-](\d{1,2})[\/\-](\d{1,4})/);
    if (dateMatch) {
      let p1 = Number(dateMatch[1]);
      let p2 = Number(dateMatch[2]);
      let p3 = Number(dateMatch[3]);

      let day, monthIdx, year;
      if (p1 > 31) {
        year = p1 < 100 ? 2000 + p1 : p1;
        monthIdx = Math.max(0, Math.min(11, p2 - 1));
        day = String(p3).padStart(2, "0");
      } else {
        day = String(p1).padStart(2, "0");
        monthIdx = Math.max(0, Math.min(11, p2 - 1));
        year = p3 < 100 ? 2000 + p3 : p3;
      }
      return `${day} ${monthNames[monthIdx]} ${year}`;
    }

    // Match "24 Jun 2026" or "24 Ags 26"
    const textMatch = str.match(/^(\d{1,2})\s+([A-Za-z]+)\s+(\d{2,4})/);
    if (textMatch) {
      const day = textMatch[1].padStart(2, "0");
      const mStr = textMatch[2].toLowerCase();
      let y = Number(textMatch[3]);
      const year = y < 100 ? 2000 + y : y;

      const monthMap = {
        jan: 0, feb: 1, mar: 2, apr: 3, mei: 4, jun: 5,
        jul: 6, agu: 7, ags: 7, sep: 8, okt: 9, nov: 10, des: 11,
        january: 0, february: 1, march: 2, april: 3, may: 4, june: 5,
        july: 6, august: 7, september: 8, october: 9, november: 10, december: 11
      };
      const mIdx = monthMap[mStr] !== undefined ? monthMap[mStr] : 0;
      return `${day} ${monthNames[mIdx]} ${year}`;
    }

    const d = new Date(raw);
    if (!isNaN(d.getTime())) {
      const day = String(d.getDate()).padStart(2, "0");
      const month = monthNames[d.getMonth()] || "Jan";
      const year = d.getFullYear();
      return `${day} ${month} ${year}`;
    }
    return str;
  };

  const splitDateTime = (raw) => {
    const monthNames = ["Jan", "Feb", "Mar", "Apr", "Mei", "Jun", "Jul", "Agu", "Sep", "Okt", "Nov", "Des"];
    if (!raw) {
      const now = new Date();
      const day = String(now.getDate()).padStart(2, "0");
      const month = monthNames[now.getMonth()];
      const year = now.getFullYear();
      const hours = String(now.getHours()).padStart(2, "0");
      const mins = String(now.getMinutes()).padStart(2, "0");
      return { date: `${day} ${month} ${year}`, time: `${hours}.${mins}` };
    }

    const str = String(raw).trim();
    if (str.includes(",")) {
      const parts = str.split(",").map((s) => s.trim());
      const dPart = parts[0] || "-";
      const tPart = parts[1] || "";
      const formattedDate = formatDateIndo(dPart);
      return { date: formattedDate, time: tPart };
    }

    if (/^\d{1,2}[\:\.]\d{2}/.test(str) && !str.includes("/") && !str.includes("-")) {
      const now = new Date();
      const day = String(now.getDate()).padStart(2, "0");
      const month = monthNames[now.getMonth()];
      const year = now.getFullYear();
      return { date: `${day} ${month} ${year}`, time: str };
    }

    const parsedDate = new Date(str);
    if (!isNaN(parsedDate.getTime())) {
      const day = String(parsedDate.getDate()).padStart(2, "0");
      const month = monthNames[parsedDate.getMonth()];
      const year = parsedDate.getFullYear();
      const hours = String(parsedDate.getHours()).padStart(2, "0");
      const mins = String(parsedDate.getMinutes()).padStart(2, "0");
      return { date: `${day} ${month} ${year}`, time: `${hours}.${mins}` };
    }

    return { date: formatDateIndo(str), time: "" };
  };

  const { date: trxDate, time: trxTime } = splitDateTime(
    meta.transactionDateTime,
  );

  const transactionNumber = meta.transactionNumber || "-";
  const cleanTxNum = formatSimplifiedTxNum(transactionNumber);
  const fullDateTime = trxDate !== "-"
    ? `${trxDate}${trxTime ? `, ${trxTime} WIB` : ""}`
    : (trxTime ? `${trxTime} WIB` : "-");
  // Paksa gunakan nama user yang sedang login/aktif nyetak, abaikan nama kasir dari meta
  const cashierName = getLoggedInUserName() || "-";
  const customerName = meta.customerName || "Walk-in";
  const customerPhone = meta.customerPhone || meta.customer_phone || "";
  const customerAddress = meta.customerAddress || meta.customer_address || "";
  const sellerType = meta.sellerType || meta.seller_type || "Internal";
  const tableName = meta.tableName || meta.table_name || meta.selected_table_name || meta.table?.name || (typeof meta.table === "string" ? meta.table : "") || (meta.transactionNotes && meta.transactionNotes.match(/Meja:\s*([^\n,-]+)/)?.[1]?.trim()) || "";
  const transactionNotes = meta.transactionNotes || "";

  // Deteksi unit kendaraan transaksi (khas Legiun, misal TRAGA 001, CAT 005)
  const detectedUnitName = detectVehicleUnit(meta, items, data);

  const subtotal = Number(totals.subtotal ?? 0);
  const total = Number(totals.total ?? subtotal);
  const tunai = Number(totals.tunai ?? total);
  const change = Number(totals.change ?? 0);
  const isDP = !!totals.isDownPayment;
  const remaining = Number(totals.remaining ?? 0);
  const dpAmount = Number(totals.dpAmount ?? 0);
  const discount = Number(totals.discount ?? totals.discountTotal ?? 0);
  const additionalCosts = Number(
    totals.additionalCosts ?? totals.additional_costs ?? totals.additional ?? 0,
  );
  // Label metode pembayaran (Tunai / Transfer / QRIS / Campuran).
  // Default ke "Tunai" agar backward-compatible.
  const paymentMethodLabel = getReceiptPaymentMethodLabel(totals);
  const paymentStatus = (() => {
    if (isDP) return "DP / Belum Lunas";
    const totalPaidInTx = tunai + dpAmount;
    if (totalPaidInTx >= total && total > 0) return "Lunas";
    if (totalPaidInTx > 0 && totalPaidInTx < total) return "Sebagian";
    return "Belum Bayar";
  })();

  const isDiscountFromType = (totals.discount_from_type !== undefined || data?.totals?.discount_from_type !== undefined)
    ? !!(totals.discount_from_type ?? data?.totals?.discount_from_type)
    : !!(
        meta.salesTypeObj?.jenis === "Diskon" ||
        meta.salesTypeObj?.jenis === "Markup" ||
        (typeof meta.sellerType === "object" && (meta.sellerType?.jenis === "Diskon" || meta.sellerType?.jenis === "Markup"))
      );

  const { discountLabel, additionalCostsLabel } = getReceiptAdjustmentLabels(data);

  const cfg = getReceiptConfig();
  const isThermal = cfg.orientation === "thermal";
  const isKuliner = localStorage.getItem("PREVIEW_APPS") === "true";
  const baseFontSize = cfg.thermalWidth === '58mm' ? '10px' : '11px';
  const fallbackLogo = localStorage.getItem('tenantLogoUrl') || "";
  const finalLogoUrl = storeLogoUrl || fallbackLogo;

  // ============ HORIZONTAL (A5 Landscape) — invoice-style ============
  if (!isThermal) {
    const grouped = groupItems(items);
    const itemRowsHtml =
      grouped && grouped.length > 0
        ? grouped
          .map((it, idx) => {
            const name = it?.name || "-";
            const qty = Number(it?.qty || 0);
            const price = Number(it?.price || 0);
            const calculatedItem = calculateOrderItemTotals(it);
            const totalIt = Number(it?.total ?? it?.total_price ?? calculatedItem.subtotal);
            const baseTotal = calculatedItem.baseSubtotal;
            // Ambil satuan fisik kemasan barang (Pcs, Liter, dll), DILARANG memasukkan nama armada kendaraan
            const unit = getPhysicalProductUnit(it, detectedUnitName, isKuliner);
            const variantSuffix = it?.variant_name ? ` (${it.variant_name})` : "";
            const itemNotes = !isKuliner ? (it?.notes || it?.note || "") : "";
            let html = `
                <tr>
                  <td class="no" style="font-weight: 600;">${idx + 1}</td>
                  <td class="name">
                    <span style="font-weight: 600;">${escapeHtml(name)}${escapeHtml(variantSuffix)}</span>
                    ${itemNotes ? `<span style="font-size: 9px; color: #d97706; font-style: italic; display: block; margin-top: 2px;">* ${escapeHtml(itemNotes)}</span>` : ""}
                  </td>
                  <td class="qty" style="font-weight: 600;">${qty}${unit ? ` ${escapeHtml(unit)}` : ""}</td>
                  <td class="price">Rp ${formatNumber(price)}</td>
                  <td class="total" style="font-weight: 700;">Rp ${formatNumber((Number(it?.discount_amount || 0) > 0 || Number(it?.additional_cost || 0) > 0) ? baseTotal : totalIt)}</td>
                </tr>
              `;

            const itemDiscount = Number(it?.discount_amount || 0);
            const itemAdditionalCost = Number(it?.additional_cost || 0);
            if (itemDiscount > 0) {
              html += `<tr style="color:#e11d48;font-size:0.9em;"><td></td><td colspan="3" style="padding-left:20px;">Diskon Item</td><td class="total" style="font-variant-numeric:tabular-nums;white-space:nowrap;">- Rp ${formatNumber(itemDiscount)}</td></tr>`;
            }
            if (itemAdditionalCost > 0) {
              html += `<tr style="color:#2563eb;font-size:0.9em;"><td></td><td colspan="3" style="padding-left:20px;">Biaya Tambahan Item</td><td class="total" style="font-variant-numeric:tabular-nums;white-space:nowrap;">+ Rp ${formatNumber(itemAdditionalCost)}</td></tr>`;
            }
            if (itemDiscount > 0 || itemAdditionalCost > 0) {
              html += `<tr style="font-size:0.9em;font-weight:700;"><td></td><td colspan="3" style="padding-left:20px;border-top:1px dotted #777;">Total Item</td><td class="total" style="border-top:1px dotted #777;font-variant-numeric:tabular-nums;white-space:nowrap;">Rp ${formatNumber(totalIt)}</td></tr>`;
            }
            
            if (it.addons && it.addons.length > 0) {
              it.addons.forEach(ad => {
                html += `
                  <tr style="background-color: #fafafa; font-style: italic; font-size: 0.9em; color: #555;">
                    <td class="no"></td>
                    <td class="name" style="padding-left: 20px;">+ ${escapeHtml(ad.name)}</td>
                    <td class="qty">${ad.qty}</td>
                    <td class="price">Rp ${formatNumber(ad.price)}</td>
                    <td class="total">Rp ${formatNumber(ad.total)}</td>
                  </tr>
                `;
              });
            }
            return html;
          })
          .join("")
        : `<tr><td colspan="5" style="text-align:center;color:#9ca3af;padding:16px;">Tidak ada item</td></tr>`;

    // Inisial untuk fallback logo
    const initial = (storeName || "T")
      .trim()
      .split(/\s+/)
      .map((w) => w.charAt(0))
      .slice(0, 2)
      .join("")
      .toUpperCase();

    return `<!doctype html>
<html lang="id">
<head>
  <meta charset="UTF-8" />
  <meta name="viewport" content="width=device-width, initial-scale=1.0" />
  <title>${escapeHtml(title)}</title>
  <style>${getReceiptPrintCss()}</style>
</head>
<body>
  <div class="wrap">
    <!-- HEADER: Store info (left) + Customer & Meta info (right) -->
    <div class="h-header">
      <div class="h-store">
        ${cfg.printLogo !== false
          ? (finalLogoUrl
              ? `<img class="logo" src="${escapeHtml(finalLogoUrl)}" alt="logo" onerror="this.style.display='none';this.nextElementSibling.style.display='flex';" /><div class="logo-fallback" style="display:none;">${escapeHtml(initial)}</div>`
              : `<div class="logo-fallback">${escapeHtml(initial)}</div>`
            )
          : ""
        }
        <div class="h-store-info">
          <h1>${escapeHtml(storeName)}</h1>
          ${storeAddress ? `<p>${escapeHtml(storeAddress)}</p>` : ""}
          ${storePhone ? `<p>${escapeHtml(storePhone)}</p>` : ""}
        </div>
      </div>
      <div class="h-customer">
        <div class="crow">
          <span class="clabel">Nama Pelanggan</span>
          <span class="csep">:</span>
          <span class="cval bold">${escapeHtml(customerName)}</span>
        </div>
        ${detectedUnitName ? `
        <div class="crow">
          <span class="clabel">Unit</span>
          <span class="csep">:</span>
          <span class="cval bold">${escapeHtml(detectedUnitName)}</span>
        </div>` : ""}
        ${(tableName && localStorage.getItem("PREVIEW_APPS") === "true")
        ? `<div class="crow">
          <span class="clabel">Meja</span>
          <span class="csep">:</span>
          <span class="cval bold">${escapeHtml(tableName)}</span>
        </div>`
        : ""
      }
        <div class="crow">
          <span class="clabel">Nomor Transaksi</span>
          <span class="csep">:</span>
          <span class="cval bold">${escapeHtml(cleanTxNum)}</span>
        </div>
        <div class="crow">
          <span class="clabel">Tanggal &amp; Waktu</span>
          <span class="csep">:</span>
          <span class="cval">${escapeHtml(fullDateTime)}</span>
        </div>
        ${sellerType ? `
        <div class="crow">
          <span class="clabel">Tipe Penjualan</span>
          <span class="csep">:</span>
          <span class="cval">${escapeHtml(sellerType)}</span>
        </div>` : ""}
      </div>
    </div>

    <hr class="h-divider" />

    <!-- ITEMS table -->
    <table class="h-items">
      <colgroup>
        <col class="col-no" style="width: 32px;" />
        <col class="col-name" />
        <col class="col-qty" style="width: 68px;" />
        <col class="col-price" style="width: 95px;" />
        <col class="col-total" style="width: 100px;" />
      </colgroup>
      <thead>
        <tr>
          <th class="th-no">No</th>
          <th class="th-name">Nama Produk</th>
          <th class="th-qty">Jumlah</th>
          <th class="th-price">Harga Satuan</th>
          <th class="th-total">Harga Total</th>
        </tr>
      </thead>
      <tbody>
        ${itemRowsHtml}
      </tbody>
    </table>

    <!-- FOOTER: signature (left) + summary (right) -->
    <div class="h-footer">
      <div class="left">
        <p class="signature">Hormat Kami</p>
        <p class="thanks">${escapeHtml(storeFooterNote || "Terima Kasih Telah Menjadi Pelanggan Kami")}</p>
        ${transactionNotes ? `<p style="font-size:9px;color:#555;margin-top:4px;"><b>Catatan:</b> ${escapeHtml(transactionNotes).replace(/\n/g, '<br/>')}</p>` : ""}
      </div>
      <div class="right">
        <div class="sum-row">
          <span class="label">Sub Total</span>
          <span class="colon">:</span>
          <span class="value">Rp ${formatNumber(subtotal)}</span>
        </div>
        <!-- [HIDDEN_SALE_TYPE_MARKUP]: Hide additional costs if from sale type -->
        ${additionalCosts > 0 && !isDiscountFromType
        ? `<div class="sum-row">
          <span class="label">${escapeHtml(additionalCostsLabel)}</span>
          <span class="colon">:</span>
          <span class="value">Rp ${formatNumber(additionalCosts)}</span>
        </div>`
        : ""
      }
        <!-- [HIDDEN_SALE_TYPE_DISCOUNT]: Hide discount if from sale type -->
        ${discount > 0 && !isDiscountFromType
        ? `<div class="sum-row">
          <span class="label">${escapeHtml(discountLabel)}</span>
          <span class="colon">:</span>
          <span class="value">- Rp ${formatNumber(discount)}</span>
        </div>`
        : ""
      }
        <div class="sum-row strong">
          <span class="label">Grand Total</span>
          <span class="colon">:</span>
          <span class="value">Rp ${formatNumber(total)}</span>
        </div>
        ${dpAmount > 0
        ? `<div class="sum-row">
          <span class="label">DP</span>
          <span class="colon">:</span>
          <span class="value">Rp ${formatNumber(dpAmount)}</span>
        </div>`
        : ""
      }
        <div class="sum-row">
          <span class="label">Metode Pembayaran</span>
          <span class="colon">:</span>
          <span class="value">${escapeHtml(paymentMethodLabel)}</span>
        </div>
        <div class="sum-row status">
          <span class="label">Status Pembayaran</span>
          <span class="colon">:</span>
          <span class="value">${escapeHtml(paymentStatus)}</span>
        </div>
      </div>
    </div>

    <p class="h-poweredby">Powered by ASAlink</p>

    <div class="actions no-print" style="margin-top: 16px; display: flex; align-items: center; justify-content: center; gap: 8px;">
      <button class="btn btn-close" onclick="window.close()" style="background: #64748b; color: #ffffff; border: none; border-radius: 6px; padding: 7px 16px; font-size: 12px; font-weight: 600; cursor: pointer;">Kembali</button>
      <button class="btn btn-print" onclick="window.print()" style="background: #0f766e; color: #ffffff; border: none; border-radius: 6px; padding: 7px 16px; font-size: 12px; font-weight: 600; cursor: pointer;">Cetak</button>
    </div>
  </div>
</body>
</html>`;
  }

  // ============ THERMAL (Vertical) — original strip layout ============
  const grouped = groupItems(items);
  const itemRows =
    grouped && grouped.length > 0
      ? grouped
        .map((it) => {
          const name = it?.name || "-";
          const qty = Number(it?.qty || 0);
          const price = Number(it?.price || 0);
          const calculatedItem = calculateOrderItemTotals(it);
          const totalIt = Number(it?.total ?? it?.total_price ?? calculatedItem.subtotal);
          const baseTotal = calculatedItem.baseSubtotal;
          const unit = it?.unit_name || (it?.unit ? (typeof it.unit === 'object' ? it.unit.name : String(it.unit)) : "");
          const variantSuffix = it?.variant_name ? ` (${it.variant_name})` : "";
          const itemNotes = !isKuliner ? (it?.notes || it?.note || "") : "";
          let html = `
              <div className="item-block">
                <div class="item-name" style="font-weight: bold;">${escapeHtml(name)}${escapeHtml(variantSuffix)}</div>
                ${itemNotes ? `<div class="item-notes" style="font-size: ${baseFontSize};font-style: italic;color:#d97706;margin-top:1px;">* ${escapeHtml(itemNotes)}</div>` : ""}
                <div class="item-line">
                  <span class="qty">${qty} X Rp ${formatNumber(price)}</span>
                  <span class="total" style="font-weight: bold;">Rp ${formatNumber((Number(it?.discount_amount || 0) > 0 || Number(it?.additional_cost || 0) > 0) ? baseTotal : totalIt)}</span>
                </div>
              </div>
            `;

          const itemDiscount = Number(it?.discount_amount || 0);
          const itemAdditionalCost = Number(it?.additional_cost || 0);
          if (itemDiscount > 0 || itemAdditionalCost > 0) {
            html += `<div class="item-adjustments">`;
            if (itemDiscount > 0) {
              html += `<div class="item-line" style="color:#e11d48;"><span>Diskon Item</span><span class="adjustment-value">- Rp ${formatNumber(itemDiscount)}</span></div>`;
            }
            if (itemAdditionalCost > 0) {
              html += `<div class="item-line" style="color:#2563eb;"><span>Biaya Tambahan Item</span><span class="adjustment-value">+ Rp ${formatNumber(itemAdditionalCost)}</span></div>`;
            }
            html += `<div class="item-line item-total-row"><span>Total Item</span><span class="adjustment-value">Rp ${formatNumber(totalIt)}</span></div>`;
            html += `</div>`;
          }
          
          if (it.addons && it.addons.length > 0) {
            it.addons.forEach(ad => {
              html += `
                <div class="item-block" style="padding-left: 14px; font-style: italic; color: #333; margin-top: 2px;">
                  <div class="item-name" style="font-weight: normal;">+ ${escapeHtml(ad.name)}</div>
                  <div class="item-line" style="padding-left: 0;">
                    <span class="qty">${ad.qty} X Rp ${formatNumber(ad.price)}</span>
                    <span class="total">Rp ${formatNumber(ad.total)}</span>
                  </div>
                </div>
              `;
            });
          }
          return html;
        })
        .join("")
      : `<div class="item-block center muted">Tidak ada item</div>`;

  return `<!doctype html>
<html lang="id">
<head>
  <meta charset="UTF-8" />
  <meta name="viewport" content="width=device-width, initial-scale=1.0" />
  <title>${escapeHtml(title)}</title>
  <style>${getReceiptPrintCss()}</style>
</head>
<body>
  <div class="wrap">
    ${finalLogoUrl && cfg.printLogo !== false ? `<img class="store-logo" src="${escapeHtml(finalLogoUrl)}" alt="logo" />` : ""}
    <h1 class="store-name">${escapeHtml(storeName)}</h1>
    ${storeAddress ? `<p class="store-sub">${escapeHtml(storeAddress)}</p>` : ""}
    ${storeTagline ? `<p class="store-sub">${escapeHtml(storeTagline)}</p>` : ""}
    ${storePhone ? `<p class="store-sub">${escapeHtml(storePhone)}</p>` : ""}

    <div class="div-dash">----------------------------------------------------------------------------------------------------------------------------------------------------------------</div>

    <div class="meta-row">
      <div style="font-weight: 600;">${escapeHtml(cleanTxNum)}</div>
      <div style="text-align: right;">${escapeHtml(fullDateTime)}</div>
    </div>
    <div class="meta-row">
      <div>Pelanggan:</div>
      <div style="text-align: right; font-weight: 600;">${escapeHtml(customerName)}</div>
    </div>
    ${detectedUnitName ? `
    <div class="meta-row">
      <div>Unit:</div>
      <div style="text-align: right; font-weight: 600;">${escapeHtml(detectedUnitName)}</div>
    </div>` : ""}
    ${sellerType ? `
    <div class="meta-row">
      <div>Tipe Penjualan:</div>
      <div style="text-align: right;">${escapeHtml(sellerType)}</div>
    </div>` : ""}
    ${(tableName && localStorage.getItem("PREVIEW_APPS") === "true")
    ? `<div class="meta-row">
      <div>Meja:</div>
      <div style="text-align: right; font-weight:bold">${escapeHtml(tableName)}</div>
    </div>`
    : ""
    }
    <div class="meta-row">
      <div>Kasir:</div>
      <div style="text-align: right;">${escapeHtml(cashierName)}</div>
    </div>

    <div class="div-dash">----------------------------------------------------------------------------------------------------------------------------------------------------------------</div>

    ${itemRows}

    <div class="div-dash">----------------------------------------------------------------------------------------------------------------------------------------------------------------</div>

    <table class="summary">
      <tr>
        <td class="label">Sub Total</td>
        <td class="val">Rp ${formatNumber(subtotal)}</td>
      </tr>
      <!-- [HIDDEN_SALE_TYPE_DISCOUNT]: Hide discount if from sale type -->
      ${discount > 0 && !isDiscountFromType
      ? `<tr>
        <td class="label">${escapeHtml(discountLabel)}</td>
        <td class="val">- Rp ${formatNumber(discount)}</td>
      </tr>`
      : ""
    }
      <!-- [HIDDEN_SALE_TYPE_MARKUP]: Hide additional costs if from sale type -->
      ${additionalCosts > 0 && !isDiscountFromType
      ? `<tr>
        <td class="label">${escapeHtml(additionalCostsLabel)}</td>
        <td class="val">+ Rp ${formatNumber(additionalCosts)}</td>
      </tr>`
      : ""
    }
      <tr class="strong">
        <td class="label">Total Tagihan</td>
        <td class="val">Rp ${formatNumber(total)}</td>
      </tr>
      ${dpAmount > 0
      ? `<tr>
        <td class="label">DP</td>
        <td class="val">Rp ${formatNumber(dpAmount)}</td>
      </tr>`
      : ""
    }
      <tr>
        <td class="label">Bayar (${isDP ? "DP" : paymentMethodLabel})</td>
        <td class="val">Rp ${formatNumber(tunai)}</td>
      </tr>
      ${isDP
      ? `<tr class="strong">
        <td class="label">Sisa Tagihan</td>
        <td class="val">Rp ${formatNumber(remaining || Math.max(total - tunai, 0))}</td>
      </tr>`
      : change > 0
        ? `<tr>
        <td class="label">Kembalian</td>
        <td class="val">Rp ${formatNumber(change)}</td>
      </tr>`
        : ""
    }
    </table>

    <div class="div-dash">----------------------------------------------------------------------------------------------------------------------------------------------------------------</div>

    ${transactionNotes ? `<div style="font-size:${baseFontSize};margin:2px 0;"><b>Catatan:</b> ${escapeHtml(transactionNotes).replace(/\n/g, '<br/>')}</div>` : ""}

    <div class="footer">
      ${storeFooterNote ? `<p class="note">${escapeHtml(storeFooterNote).replace(/\n/g, '<br/>')}</p>` : ""}
      <p>Powered by ASAlink</p>
    </div>

    <div class="actions no-print" style="margin-top: 16px; display: flex; align-items: center; justify-content: center; gap: 8px;">
      <button class="btn btn-close" onclick="window.close()" style="background: #64748b; color: #ffffff; border: none; border-radius: 6px; padding: 7px 16px; font-size: 12px; font-weight: 600; cursor: pointer;">Kembali</button>
      <button class="btn btn-print" onclick="window.print()" style="background: #0f766e; color: #ffffff; border: none; border-radius: 6px; padding: 7px 16px; font-size: 12px; font-weight: 600; cursor: pointer;">Cetak</button>
    </div>
  </div>
</body>
</html>`;
}

export function buildShiftReportHtml(data) {
  const {
    storeName = "Toko",
    shift = {},
  } = data || {};

  const summary = shift.summary || {};
  const cfg = getReceiptConfig();
  const initial = (storeName || "T")
    .trim()
    .split(/\s+/)
    .map((w) => w.charAt(0))
    .slice(0, 2)
    .join("")
    .toUpperCase();

  const fmt = (n) => Number(n || 0).toLocaleString("id-ID");
  const formatDateOnly = (iso) => {
    if (!iso) return "-";
    try {
      return new Date(iso).toLocaleString("id-ID", {
        day: "2-digit", month: "short", year: "numeric",
      });
    } catch { return iso; }
  };
  const formatTimeOnly = (iso) => {
    if (!iso) return "-";
    try {
      return new Date(iso).toLocaleString("id-ID", {
        hour: "2-digit", minute: "2-digit"
      }) + " WIB";
    } catch { return iso; }
  };

  const revenueMethods = summary.revenue_by_payment_method || [];
  const totalPenjualan = revenueMethods.reduce((s, m) => s + Number(m.total || 0), 0);

  const pendingTxnCount = Number(summary.total_transaction_pending || 0);
  const cancelledTxnCount = Number(summary.total_transaction_cancelled || 0);
  const doneTxnCount = Number(summary.total_transaction_done || 0);
  const totalTxnCount = Number(summary.total_transaction_receipt || 0);

  const METHOD_LABEL = {
    CASH: "Tunai",
    QRIS: "QRIS",
    TRANSFER: "Transfer",
    MIXED: "Campuran",
  };

  let revenueHtml = "";
  revenueMethods.forEach(m => {
    const label = METHOD_LABEL[m.method] || m.method;
    const countLabel = m.count ? ` (${m.count})` : "";
    revenueHtml += `
      <div class="meta-row" style="margin-top:2px;">
        <div style="padding-left:8px;">&bull; ${escapeHtml(label)}${escapeHtml(countLabel)}</div>
        <div>Rp ${fmt(m.total)}</div>
      </div>
    `;
  });

  const notesText = shift.notes ? shift.notes : "Tidak ada catatan pada saat tutup kasir.";

  // Thermal layout
  return `<!doctype html>
<html lang="id">
<head>
  <meta charset="UTF-8" />
  <meta name="viewport" content="width=device-width, initial-scale=1.0" />
  <title>Laporan Shift - ${escapeHtml(shift.shift_number)}</title>
  <style>${getReceiptPrintCss()}</style>
</head>
<body>
  <div class="wrap">
    <div style="text-align:left;">
      <h1 class="store-name" style="text-align:left; margin-bottom:4px;">${escapeHtml(storeName)}</h1>
      <div style="font-size:12px; margin-bottom:8px; display:flex; align-items:center; gap:6px;">
        <span>Nota Tutup Kasir</span> <span style="color:#aaa;">|</span> <span>Settlement</span>
      </div>
    </div>

    <div class="div-dash">----------------------------------------------------------------------------------------------------------------------------------------------------------------</div>

    <div class="meta-row">
      <div>Dibuka Oleh</div>
      <div style="font-weight:bold">${escapeHtml(shift.opened_by_name || shift.opened_by || "-")}</div>
    </div>
    <div class="meta-row" style="margin-top:2px;">
      <div>Tanggal Buka</div>
      <div style="font-weight:bold">${escapeHtml(formatDateOnly(shift.opened_at))}</div>
    </div>
    <div class="meta-row" style="margin-top:2px;">
      <div>Waktu Buka</div>
      <div style="font-weight:bold">${escapeHtml(formatTimeOnly(shift.opened_at))}</div>
    </div>

    <div class="div-dash">----------------------------------------------------------------------------------------------------------------------------------------------------------------</div>

    <div class="meta-row">
      <div>Modal Awal</div>
      <div style="font-weight:bold">Rp ${fmt(shift.opening_cash || shift.start_cash)}</div>
    </div>
    <div class="meta-row" style="margin-top:2px;">
      <div>Total Penjualan</div>
      <div style="font-weight:bold">Rp ${fmt(totalPenjualan)}</div>
    </div>
    ${revenueHtml}

    <div class="meta-row" style="margin-top:6px;">
      <div>Total Transaksi</div>
      <div style="font-weight:bold">${fmt(totalTxnCount)} Nota</div>
    </div>
    <div class="meta-row" style="margin-top:2px;">
      <div style="padding-left:8px;">&bull; Transaksi Pending</div>
      <div>${fmt(pendingTxnCount)} Nota</div>
    </div>
    <div class="meta-row" style="margin-top:2px;">
      <div style="padding-left:8px;">&bull; Transaksi Dibatalkan</div>
      <div>${fmt(cancelledTxnCount)} Nota</div>
    </div>
    <div class="meta-row" style="margin-top:2px;">
      <div style="padding-left:8px;">&bull; Transaksi Selesai</div>
      <div>${fmt(doneTxnCount)} Nota</div>
    </div>

    <div class="meta-row" style="margin-top:6px;">
      <div>Total Pengeluaran</div>
      <div style="font-weight:bold">Rp ${fmt(summary.total_cash_out || 0)}</div>
    </div>
    <div class="meta-row" style="margin-top:2px;">
      <div>Total Kas Kasir</div>
      <div style="font-weight:bold">Rp ${fmt(summary.total_cashier_cash || 0)}</div>
    </div>

    <div class="div-dash">----------------------------------------------------------------------------------------------------------------------------------------------------------------</div>

    <div class="meta-row">
      <div>Ditutup Oleh</div>
      <div style="font-weight:bold">${escapeHtml(shift.closed_by_name || "-")}</div>
    </div>
    <div class="meta-row" style="margin-top:2px;">
      <div>Tanggal Tutup</div>
      <div style="font-weight:bold">${escapeHtml(formatDateOnly(shift.closed_at || new Date().toISOString()))}</div>
    </div>
    <div class="meta-row" style="margin-top:2px;">
      <div>Waktu Tutup</div>
      <div style="font-weight:bold">${escapeHtml(formatTimeOnly(shift.closed_at || new Date().toISOString()))}</div>
    </div>

    <div class="div-dash">----------------------------------------------------------------------------------------------------------------------------------------------------------------</div>

    <div class="meta-row">
      <div>Jumlah Disetorkan</div>
      <div style="font-weight:bold">Rp ${fmt(shift.closing_cash || 0)}</div>
    </div>
    <div class="meta-row" style="margin-top:2px;">
      <div>Sisa Uang Kas Kasir</div>
      <div style="font-weight:bold">Rp ${fmt(Math.max(0, (summary.total_cashier_cash || 0) - (shift.closing_cash || 0)))}</div>
    </div>

    <div style="margin-top: 12px; font-size: 11px; color: #000;">
      ${escapeHtml(notesText).replace(/\n/g, '<br/>')}
    </div>

    <div class="footer" style="margin-top: 12px;">
      <p style="text-align:center;">Powered by ASAlink</p>
    </div>

    <div class="actions no-print" style="margin-top: 16px; display: flex; align-items: center; justify-content: center; gap: 8px;">
      <button class="btn btn-close" onclick="window.close()" style="background: #64748b; color: #ffffff; border: none; border-radius: 6px; padding: 7px 16px; font-size: 12px; font-weight: 600; cursor: pointer;">Kembali</button>
      <button class="btn btn-print" onclick="window.print()" style="background: #0f766e; color: #ffffff; border: none; border-radius: 6px; padding: 7px 16px; font-size: 12px; font-weight: 600; cursor: pointer;">Cetak</button>
    </div>
  </div>
</body>
</html>`;
}

export function buildOrderReceiptHtml(data, options = {}) {
  const { customTitle, bodyOnly } = options;
  const {
    storeName = "Toko",
    storeFooterNote = "Dicetak untuk persiapan pesanan",
    meta = {},
    items = [],
  } = data || {};

  const splitDateTime = (raw) => {
    if (!raw) return { date: "-", time: "" };
    const str = String(raw).trim();
    if (str.includes(",")) {
      const [d, t] = str.split(",").map((s) => s.trim());
      return { date: d || "-", time: t || "" };
    }
    const match = str.match(/^(.*?)\s+(\d{2}[.:]\d{2}(?::\d{2})?)$/);
    if (match) {
      return { date: match[1].trim() || "-", time: match[2].trim() || "" };
    }
    return { date: str, time: "" };
  };
  const transactionNumber = meta.transactionNumber || "-";
  const cleanTxNum = formatSimplifiedTxNum(transactionNumber);
  const formattedOrderTime = formatOrderDateTime(
    meta.transactionDateTime ||
      meta.orderDate ||
      meta.created_at ||
      meta.createdAt,
  );
  // Paksa gunakan nama user yang sedang login/aktif nyetak, abaikan nama kasir dari meta
  const cashierName = getLoggedInUserName() || "-";
  const sellerType = meta.sellerType || meta.seller_type || "Internal";
  const transactionNotes = meta.transactionNotes || "";
  const detectedUnitName = detectVehicleUnit(meta, items, data);

  const cfg = getReceiptConfig();
  const isThermal = cfg.orientation === "thermal";
  const thermalWidth = cfg.thermalWidth || "80mm";
  const wrapWidth = isThermal 
    ? (thermalWidth === "58mm" ? "185px" : "275px") 
    : "140mm"; // Horizontal
  const baseFontSize = thermalWidth === "58mm" ? "10px" : "11px";

  const grouped = groupItems(items);
  const itemRows =
    grouped && grouped.length > 0
      ? grouped
        .map((it, idx) => {
          const name = it?.name || it?.item_name || it?.product_name || "-";
          const qty = Number(it?.qty || it?.quantity || 0);
          const variantSuffix = it?.variant_name ? ` (${it.variant_name})` : "";
          const itemNotes = it?.notes || it?.note || "";
          
          let html = `
              <div class="item-block" style="margin: 0; padding: 1px 0; font-size: ${baseFontSize}; line-height: 1.25;">
                <div class="item-name" style="font-weight: bold;">${qty}x ${escapeHtml(name)}${escapeHtml(variantSuffix)}</div>
                ${itemNotes ? `<div class="item-notes" style="font-size: ${baseFontSize}; font-style: italic; color: #d97706; font-weight: bold; margin-top: 1px;">* ${escapeHtml(itemNotes)}</div>` : ""}
              </div>
            `;
          
          if (it.addons && it.addons.length > 0) {
            it.addons.forEach(ad => {
              html += `
                <div class="item-block" style="margin: 0; padding: 1px 0 1px 15px; font-size: ${baseFontSize}; font-style: italic; color: #333;">
                  <div class="item-name">+ ${escapeHtml(ad.name)} (x${ad.qty})</div>
                </div>
              `;
            });
          }
          return html;
        })
        .join("")
      : `<div class="item-block center muted" style="margin-bottom: 0;">Tidak ada item</div>`;

  const htmlHead = `
<!DOCTYPE html>
<html>
<head>
  <meta charset="utf-8" />
  <title>Nota Pesanan</title>
  <style>
    @page { size: ${isThermal ? thermalWidth : '140mm'} auto; margin: 0; }
    html, body { margin: 0; padding: 0; }
    body {
      font-family: 'Menlo', 'Consolas', 'Courier New', monospace;
      font-size: ${baseFontSize};
      color: #111;
      padding: 8px;
      background: #f4f4f4;
    }
    .wrap {
      width: ${wrapWidth};
      max-width: 100%;
      margin: 0 auto;
      background: #fff;
      border: 1px solid #e5e5e5;
      padding: 3mm;
      box-sizing: border-box;
    }
    .center { text-align: center; }
    .div-dash { display: block; width: 100%; overflow: hidden; white-space: nowrap; color: #555; padding: 4px 0; margin: 0; box-sizing: border-box; letter-spacing: 1px; }
    .meta-row { display: flex; justify-content: space-between; gap: 8px; font-size: ${baseFontSize}; line-height: 1.45; }
    .footer { text-align: center; margin-top: 0; font-size: 10px; color: #333; }
    .footer p { margin: 2px 0; }
    .actions { margin-top: 14px; display: flex; justify-content: center; }
    .btn { border: none; background: #111; color: #fff; border-radius: 6px; padding: 8px 14px; cursor: pointer; font-family: inherit; }
    @media print {
      body { 
        padding: 0; background: #fff; -webkit-print-color-adjust: exact; print-color-adjust: exact; 
      }
      .wrap { width: ${wrapWidth}; min-height: auto; border: none; padding: 0; margin: 0 auto; overflow: hidden; }
      .actions { display: none; }
    }
  </style>
</head>
<body>
`;

  const isAdditional = !!(meta.isAdditional || meta.isAdditionalOrder || meta.is_additional);

  const htmlBody = `
  <div class="wrap">
    <p class="store-sub" style="text-align: center; font-size: 11px; font-weight: bold; margin: 0 0 6px 0;">${escapeHtml((customTitle || 'Nota Pesanan').trim())}</p>

    <div class="div-dash">----------------------------------------------------------------------------------------------------------------------------------------------------------------</div>

    <div class="meta-row">
      <div>No. ${escapeHtml(cleanTxNum)}</div>
      ${isAdditional ? `<div style="font-weight: bold;">Tambahan</div>` : ""}
    </div>
    <div class="meta-row" style="margin-top: 2px;">
      <span style="white-space: nowrap;">Pelanggan:</span>
      <span style="text-align: right; word-break: break-word;">${escapeHtml(meta.customerName || "-")}</span>
    </div>
    <div class="meta-row" style="margin-top: 2px;">
      <span style="white-space: nowrap;">Waktu:</span>
      <span style="text-align: right; word-break: break-word;">${escapeHtml(formattedOrderTime)}</span>
    </div>
    <div class="meta-row" style="margin-top: 2px;">
      <span style="white-space: nowrap;">Tipe Penjualan:</span>
      <span style="text-align: right; word-break: break-word;">${escapeHtml(sellerType)}</span>
    </div>
    ${detectedUnitName ? `
    <div class="meta-row" style="margin-top: 2px;">
      <span style="white-space: nowrap;">Unit:</span>
      <span style="text-align: right; word-break: break-word; font-weight: bold;">${escapeHtml(detectedUnitName)}</span>
    </div>` : ""}
    ${(() => {
      const isApps = localStorage.getItem("PREVIEW_APPS") === "true";
      const orderTableName = meta.tableName || meta.table_name || meta.selected_table_name || meta.table?.name || (typeof meta.table === "string" ? meta.table : "") || (meta.transactionNotes && meta.transactionNotes.match(/Meja:\s*([^\n,-]+)/)?.[1]?.trim()) || "";
      return (isApps && orderTableName)
        ? `<div class="meta-row" style="margin-top: 2px; font-weight: bold; font-size: 13px;">
          <span style="white-space: nowrap;">MEJA:</span>
          <span style="text-align: right; word-break: break-word;">${escapeHtml(orderTableName)}</span>
        </div>`
        : "";
    })()}

    <div class="div-dash">----------------------------------------------------------------------------------------------------------------------------------------------------------------</div>

    <div style="min-height: auto; margin-bottom: 8px;">
      ${itemRows}
    </div>

    ${transactionNotes ? `<div style="font-size:${baseFontSize};margin:4px 0 2px 0;"><b>Catatan:</b> ${escapeHtml(transactionNotes).replace(/\n/g, '<br/>')}</div>` : ""}

    <div class="footer">
    </div>

    <div class="actions no-print" style="margin-top: 16px; display: flex; align-items: center; justify-content: center; gap: 8px;">
      <button class="btn btn-close" onclick="window.close()" style="background: #64748b; color: #ffffff; border: none; border-radius: 6px; padding: 7px 16px; font-size: 12px; font-weight: 600; cursor: pointer;">Kembali</button>
      <button class="btn btn-print" onclick="window.print()" style="background: #0f766e; color: #ffffff; border: none; border-radius: 6px; padding: 7px 16px; font-size: 12px; font-weight: 600; cursor: pointer;">Cetak</button>
    </div>
  </div>
`;

  const htmlFooter = `
</body>
</html>`;

  if (options.bodyOnly) {
    return htmlBody;
  }
  return htmlHead + htmlBody + htmlFooter;
}

// ============================================================================
// LABEL PESANAN (Order Item Label) — Kuliner Only
// Format: 1 label per item, stiker compact, font monospace
// Layout:
//   [Nama Toko]          [Tipe Penjualan]
//   [Nama Pelanggan]              [X/Total]
//   [Qty] [Satuan]
//   [Nama Produk BESAR]
//   [Varian jika ada]
//   [Catatan item]
//   [+ Addon (xN)]
//   [DD/MM/YY HH:MM]
// ============================================================================

export function getLabelPrintCss(paperWidth = '58mm') {
  const wrapWidth = paperWidth === '58mm' ? '185px' : '275px';
  return `
    @page { size: ${paperWidth} auto; margin: 0; }
    html, body { margin: 0; padding: 0; }
    body {
      font-family: 'Courier New', 'Menlo', 'Consolas', monospace;
      font-size: 11px;
      color: #000;
      padding: 0;
      background: #fff;
      -webkit-print-color-adjust: exact;
      color-adjust: exact;
    }
    .label-wrap {
      width: ${wrapWidth};
      max-width: 100%;
      margin: 0 auto;
      padding: 10px 8px 12px 8px;
      box-sizing: border-box;
      page-break-after: always;
      line-height: 1.45;
    }
    .label-wrap:last-child { page-break-after: avoid; }
    .row-split { display: flex; justify-content: space-between; align-items: baseline; gap: 4px; margin-bottom: 2px; }
    .store-name { font-size: 10px; color: #444; }
    .sales-type { font-size: 10px; color: #444; white-space: nowrap; }
    .customer-name { font-size: 13.5px; font-weight: 700; flex: 1; min-width: 0; word-break: break-word; margin-bottom: 2px; margin-top: 2px; }
    .counter { font-size: 11px; font-weight: 600; white-space: nowrap; }
    .qty-line { font-size: 11.5px; margin-top: 2px; margin-bottom: 2px; font-weight: 600; }
    .product-name { font-size: 14.5px; font-weight: 700; margin-top: 2px; margin-bottom: 3px; word-break: break-word; line-height: 1.3; }
    .addon-line { font-size: 10.5px; color: #444; margin-top: 2px; margin-bottom: 2px; }
    .notes { font-size: 10.5px; color: #333; margin-top: 3px; margin-bottom: 4px; font-weight: 600; }
    .datetime { font-size: 9.5px; color: #555; }
    .divider { border: none; border-top: 1px dashed #bbb; margin: 8px 0; }
    .actions { margin-top: 14px; display: flex; justify-content: center; gap: 8px; }
    @media print { .actions { display: none !important; } }
  `;
}

/**
 * Bangun HTML satu blok label untuk satu item.
 * @param {Object} labelData
 * @param {string} labelData.storeName
 * @param {string} labelData.customerName
 * @param {string} labelData.salesTypeName
 * @param {string} labelData.productName
 * @param {string} labelData.variantName
 * @param {number} labelData.qty
 * @param {string} labelData.unit
 * @param {string} labelData.notes
 * @param {Array}  labelData.addons  — [{ name, qty }]
 * @param {string} labelData.datetime  — "DD/MM/YY HH:MM"
 * @param {number} labelData.index     — 1-based
 * @param {number} labelData.total     — total items
 */
function buildSingleLabelHtml(labelData) {
  const {
    storeName = '', customerName = '', salesTypeName = '',
    productName = '', variantName = '', qty = 1, unit = '',
    notes = '', addons = [], datetime = '', index = 1, total = 1,
  } = labelData;

  const esc = (s) => String(s || '').replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
  const counter = `${index}/${total}`;
  const qtyUnit = [qty, unit].filter(Boolean).join(' ');

  const fullProductName = variantName ? `${productName} (${variantName})` : productName;

  const cleanAddonName = (rawName, pName) => {
    let name = String(rawName || '').trim();
    const parentName = String(pName || '').trim();
    if (parentName && name.toLowerCase().startsWith(parentName.toLowerCase() + ' - ')) {
      return name.substring(parentName.length + 3);
    }
    if (name.includes(' - ')) {
      const parts = name.split(' - ');
      return parts[parts.length - 1];
    }
    return name;
  };

  const addonLines = (addons || [])
    .filter(a => a && a.name)
    .map(a => `<div class="addon-line">+ ${esc(cleanAddonName(a.name, productName))}${a.qty > 1 ? ` (x${a.qty})` : ''}</div>`)
    .join('');

  // Tanpa kata 'Catatan:', langsung isi catatannya saja
  const cleanNote = notes ? String(notes).replace(/^catatan:\s*/i, '').trim() : '';

  return `
    <div class="label-wrap">
      <div class="row-split">
        <span class="store-name">${esc(storeName)}</span>
        <span class="sales-type">${esc(salesTypeName)}</span>
      </div>
      <div class="customer-name">${esc(customerName)}</div>
      ${qtyUnit ? `<div class="qty-line">${esc(qtyUnit)}</div>` : ''}
      <div class="product-name">${esc(fullProductName)}</div>
      ${addonLines}
      ${cleanNote ? `<div class="notes">* ${esc(cleanNote)}</div>` : ''}
      <div style="height: 10px;"></div>
      <div class="row-split" style="margin-top: 6px;">
        <span class="datetime">${esc(datetime)}</span>
        <span class="counter">${esc(counter)}</span>
      </div>
    </div>
  `;
}

/**
 * Build HTML page containing all label blocks for a transaction.
 * @param {Array} labelDataArray - array of labelData objects (one per print unit)
 * @param {string} paperWidth - '58mm' | '80mm'
 */
export function buildLabelHtml(labelDataArray, paperWidth = '58mm') {
  const css = getLabelPrintCss(paperWidth);
  const labelsHtml = (labelDataArray || []).map(buildSingleLabelHtml).join('');
  const actionBar = `<div class="actions"><button onclick="window.close()" style="background:#475569;color:#fff;border:none;border-radius:6px;padding:6px 14px;font-size:12px;font-weight:600;cursor:pointer;">Kembali</button><button onclick="window.print()" style="background:#0f766e;color:#fff;border:none;border-radius:6px;padding:6px 14px;font-size:12px;font-weight:600;cursor:pointer;">Cetak Label</button></div>`;
  return `<!DOCTYPE html><html><head><title>Label Pesanan</title><style>${css}</style></head><body onload="window.print()">${actionBar}${labelsHtml}</body></html>`;
}

const normalizePublicUrl = (url) => url ? (url.startsWith('http') ? url : `https://${url}`) : '';

export function buildCustomerQRReceiptHtml(data = {}, config = {}) {
  const {
    paper = "58mm",
  } = config;

  const {
    storeName = "ASAlink POS",
    storeAddress = "",
    storePhone = "",
    customerName = "-",
    customerPhone = "-",
    qrUrl = "",
    qrImageUrl = "",
    expiresAt = "",
    expiryMinutes = 10,
    createdAt = new Date().toLocaleString("id-ID", { dateStyle: "medium", timeStyle: "short" }),
  } = data;

  const finalQrUrl = normalizePublicUrl(qrUrl);
  const qrSrc = qrImageUrl || `https://api.qrserver.com/v1/create-qr-code/?size=260x260&format=png&color=000000&bgcolor=ffffff&qzone=1&data=${encodeURIComponent(finalQrUrl)}`;
  const esc = (s) => String(s || "").replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;");

  const css = `
    * { box-sizing: border-box; }
    body {
      font-family: 'Menlo', 'Consolas', 'Courier New', monospace;
      font-size: 12px;
      color: #000;
      padding: 8px;
      margin: 0;
      background: #fff;
    }
    .wrap {
      width: ${paper === "58mm" ? "48mm" : "72mm"};
      max-width: 100%;
      margin: 0 auto;
      text-align: center;
      padding: 2mm;
    }
    .store-name { font-size: 17px; font-weight: bold; margin-bottom: 2px; }
    .store-sub { font-size: 10px; color: #444; margin-bottom: 4px; }
    .divider { border-top: 1px dashed #444; margin: 6px 0; }
    .title { font-size: 13px; font-weight: bold; margin: 4px 0; text-transform: uppercase; }
    .info-table { width: 100%; font-size: 11px; text-align: left; margin: 4px 0; border-collapse: collapse; }
    .info-table td { padding: 2px 0; vertical-align: top; }
    .info-table td.lbl { width: 40%; color: #333; }
    .info-table td.val { font-weight: 600; }
    .qr-img { width: 180px; height: 180px; margin: 6px auto; display: block; border-radius: 4px; }
    .expiry-box { font-size: 11px; font-weight: bold; color: #b91c1c; margin: 4px 0; }
    .footer-note { font-size: 10px; color: #444; margin-top: 6px; line-height: 1.35; }
    @media print {
      body { padding: 0 !important; }
      .wrap { border: none !important; padding: 0 !important; width: 100% !important; }
      .no-print, .actions { display: none !important; }
    }
  `;

  return `<!DOCTYPE html>
<html>
<head>
  <meta charset="utf-8">
  <title>QR E-Catalog - ${esc(customerName)}</title>
  <style>${css}</style>
</head>
<body onload="window.print()">
  <div class="wrap">
    <div class="store-name">${esc(storeName)}</div>
    ${storeAddress ? `<div class="store-sub">${esc(storeAddress)}</div>` : ""}
    ${storePhone ? `<div class="store-sub">${esc(storePhone)}</div>` : ""}
    <div class="divider"></div>
    <div class="title">QR E-CATALOG PELANGGAN</div>
    <div class="divider"></div>
    <table class="info-table">
      <tr>
        <td class="lbl">Pelanggan:</td>
        <td class="val">${esc(customerName)}</td>
      </tr>
      <tr>
        <td class="lbl">Waktu Cetak:</td>
        <td class="val">${esc(createdAt)}</td>
      </tr>
      <tr>
        <td class="lbl">Masa Berlaku:</td>
        <td class="val">${expiryMinutes} Menit</td>
      </tr>
    </table>
    <div class="divider"></div>
    <img class="qr-img" src="${qrSrc}" alt="QR E-Catalog" />
    <div class="expiry-box">Berlaku s/d: ${esc(expiresAt)}</div>
    <div class="divider"></div>
    <div class="footer-note">
      Silakan scan QR code di atas untuk memesan via E-Catalog.
    </div>
    <div class="divider"></div>
    <div style="font-size: 10px; color: #666; text-align: center; margin-top: 4px;">Powered by ASAlink</div>
  </div>
</body>
</html>`;
}

export function buildReturnReceiptHtml(returnData) {
  const config = getReceiptConfig();
  const isThermal = config.orientation === "thermal";
  const paper = isThermal ? config.thermalWidth : "58mm";
  const printableWidth = paper === "80mm" ? "72mm" : "48mm";
  const dashCount = paper === "80mm" ? 42 : 32;
  const dash = "-".repeat(dashCount);
  const baseFontSize = paper === "80mm" ? "12px" : "11px";

  const storeName = localStorage.getItem("tenantName") || returnData?.storeName || "Toko";
  const storeAddress = localStorage.getItem("tenantAddress") || returnData?.storeAddress || "";
  const storePhone = localStorage.getItem("tenantPhone") || returnData?.storePhone || "";
  const logoUrl = config.printLogo ? (localStorage.getItem("tenantLogo") || returnData?.storeLogoUrl || returnData?.logoUrl || "") : "";

  const returnNumber = returnData?.return_number || "RTR-";
  const refTx = returnData?.transaction_number || returnData?.ref_transaction_number || "-";
  const customerName = returnData?.customer_name || "Pelanggan Umum";
  const returnDate = returnData?.return_date
    ? new Date(returnData.return_date).toLocaleDateString("id-ID", {
        day: "2-digit",
        month: "short",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit",
      }) + " WIB"
    : "-";
  const reason = returnData?.reason || "-";
  const items = returnData?.items || [];
  const totalRefund = Number(returnData?.total_refund_amount || 0);
  const refundMethod = returnData?.refund_method === "CASH" ? "Tunai" : (returnData?.refund_method || "Tunai");
  const cashierName = localStorage.getItem("userName") || returnData?.created_by_name || "-";

  const esc = (s) => String(s ?? "").replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;");
  const fmt = (n) => Number(n || 0).toLocaleString("id-ID");

  // =========================================================================
  // 1. HORIZONTAL LAYOUT (A5 Landscape) — standard invoice style
  // =========================================================================
  if (!isThermal) {
    const tableRowsHtml = items.map((itm, idx) => {
      const itemName = esc(itm.item_name || itm.name || "Item");
      const qty = itm.quantity || 1;
      const unit = esc(itm.unit_name || "PCS");
      const price = fmt(itm.unit_price || 0);
      const total = fmt(itm.total_price ?? (itm.unit_price * qty));
      return `
        <tr>
          <td class="no">${idx + 1}</td>
          <td class="name">${itemName}</td>
          <td class="qty">${qty} ${unit}</td>
          <td class="price">Rp ${price}</td>
          <td class="total">Rp ${total}</td>
        </tr>
      `;
    }).join("");

    return `<!DOCTYPE html>
<html>
<head>
  <meta charset="utf-8">
  <title>Bukti Retur Penjualan - ${esc(returnNumber)}</title>
  <style>
    @page { size: A5 landscape; margin: 0; }
    html, body {
      margin: 0;
      padding: 0;
      background: #f8fafc;
      -webkit-print-color-adjust: exact;
      print-color-adjust: exact;
    }
    body {
      font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif;
      font-size: 10px;
      color: #0f172a;
      padding: 10px 0;
      -webkit-font-smoothing: antialiased;
      -moz-osx-font-smoothing: grayscale;
      text-rendering: geometricPrecision;
    }
    .wrap {
      width: 196mm;
      max-width: 196mm;
      margin: 0 auto;
      background: #fff;
      min-height: auto;
      border: none;
      padding: 5mm 6mm;
      box-sizing: border-box;
      box-shadow: 0 1px 3px rgba(0,0,0,0.05);
    }
    .h-header {
      display: grid;
      grid-template-columns: 1fr 1fr;
      gap: 12px;
      margin-bottom: 6px;
      align-items: start;
    }
    .h-store { display: flex; gap: 10px; align-items: flex-start; }
    .h-store .logo {
      width: 44px; height: 44px; flex-shrink: 0;
      border-radius: 50%; object-fit: cover;
      border: 1px solid #e5e5e5;
    }
    .h-store .logo-fallback {
      width: 44px; height: 44px; flex-shrink: 0;
      border-radius: 50%; background: #f3f4f6;
      display: flex; align-items: center; justify-content: center;
      font-weight: 700; font-size: 14px; color: #6b7280;
    }
    .h-store-info h1 {
      font-size: 14px; font-weight: 800; margin: 0 0 2px 0;
      letter-spacing: 0.3px; color: #0f172a;
    }
    .h-store-info p { font-size: 8.5px; color: #475569; margin: 1px 0; line-height: 1.35; max-width: 240px; }
    .h-customer { font-size: 9.5px; line-height: 1.5; }
    .h-customer .crow {
      display: flex;
      justify-content: flex-end;
      gap: 2px;
      margin-bottom: 2px;
    }
    .h-customer .crow .clabel {
      color: #475569;
      text-align: left;
      width: 115px;
      white-space: nowrap;
      flex-shrink: 0;
    }
    .h-customer .crow .csep {
      color: #475569;
      width: 12px;
      text-align: center;
      flex-shrink: 0;
    }
    .h-customer .crow .cval {
      text-align: left;
      color: #0f172a;
      width: 135px;
      flex-shrink: 0;
      word-break: break-word;
    }
    .h-customer .crow .cval.bold { font-weight: 700; }
    .h-divider {
      border: 0;
      border-top: 1px solid #000000;
      margin: 6px 0;
    }
    .h-items {
      width: 100%;
      border-collapse: collapse;
      margin-top: 4px;
      font-size: 10px;
      border: 1px solid #000000;
      table-layout: fixed;
    }
    .h-items col.col-no { width: 32px; }
    .h-items col.col-name { width: auto; }
    .h-items col.col-qty { width: 70px; }
    .h-items col.col-price { width: 100px; }
    .h-items col.col-total { width: 105px; }
    .h-items th {
      background: #f1f5f9;
      font-weight: 700;
      padding: 5px 6px;
      text-align: center;
      border: 1px solid #000000;
      color: #000000;
      font-size: 10.5px;
      box-sizing: border-box;
    }
    .h-items th.th-no, .h-items td.no { width: 32px; max-width: 32px; text-align: center; font-weight: 600; border: 1px solid #000000; }
    .h-items th.th-name, .h-items td.name { text-align: left; font-weight: 600; border: 1px solid #000000; }
    .h-items th.th-qty, .h-items td.qty { width: 70px; max-width: 70px; text-align: center; font-weight: 600; border: 1px solid #000000; }
    .h-items th.th-price, .h-items td.price { width: 100px; max-width: 100px; text-align: right; border: 1px solid #000000; }
    .h-items th.th-total, .h-items td.total { width: 105px; max-width: 105px; text-align: right; font-weight: 700; color: #000000; border: 1px solid #000000; }
    .h-items td {
      padding: 4px 6px;
      border: 1px solid #000000;
      vertical-align: middle;
      color: #000000;
      box-sizing: border-box;
    }
    .h-footer {
      display: grid;
      grid-template-columns: 1fr 1fr;
      gap: 12px;
      margin-top: 8px;
      align-items: start;
    }
    .h-footer .left { font-size: 10px; }
    .h-footer .left .signature { font-weight: 700; margin: 0 0 4px 0; color: #000000; }
    .h-footer .left .thanks { font-size: 9px; color: #4b5563; font-style: italic; border-top: 1px dashed #cbd5e1; padding-top: 4px; margin-top: 4px; }
    .h-footer .right { font-size: 10px; }
    .h-footer .right .sum-row {
      display: flex;
      justify-content: flex-end;
      gap: 2px;
      padding: 2px 0;
      border-bottom: 1px dashed #cbd5e1;
    }
    .h-footer .right .sum-row.strong { font-weight: 700; border-bottom: 1px solid #000000; }
    .h-footer .right .sum-row .label { color: #4b5563; width: 135px; text-align: left; }
    .h-footer .right .sum-row .colon { color: #4b5563; width: 12px; text-align: center; }
    .h-footer .right .sum-row .value { text-align: right; color: #111; font-weight: 600; width: 115px; }
    .h-footer .right .sum-row.strong .value { font-weight: 700; color: #0f172a; }
    .h-footer .right .sum-row.status .value { font-weight: 700; color: #7e22ce; }
    .h-poweredby { text-align: center; font-size: 8.5px; color: #94a3b8; margin: 8px 0 0 0; }
    .actions, .no-print { margin-top: 14px; display: flex; justify-content: center; align-items: center; gap: 10px; }
    .btn { border: none; background: #0f766e; color: #fff; border-radius: 6px; padding: 8px 16px; cursor: pointer; font-weight: 600; font-size: 12px; }
    .btn-close { background: #64748b; }
    @media print {
      * {
        -webkit-print-color-adjust: exact !important;
        print-color-adjust: exact !important;
        text-rendering: geometricPrecision !important;
      }
      @page {
        size: A5 landscape;
        margin: 0;
      }
      html, body {
        width: 210mm !important;
        max-width: 210mm !important;
        min-width: 210mm !important;
        margin: 0 auto !important;
        padding: 0 !important;
        background: #ffffff !important;
      }
      body {
        font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif !important;
        color: #000000 !important;
      }
      .wrap {
        width: 196mm !important;
        max-width: 196mm !important;
        min-width: 196mm !important;
        margin: 3mm auto 0 auto !important;
        padding: 5mm 6mm !important;
        border: none !important;
        box-shadow: none !important;
        box-sizing: border-box !important;
        page-break-inside: avoid !important;
        break-inside: avoid !important;
      }
      .actions, .no-print { display: none !important; }
      .h-items {
        width: 100% !important;
        border: 1px solid #000000 !important;
        table-layout: fixed !important;
      }
      .h-items col.col-no { width: 32px !important; }
      .h-items col.col-name { width: auto !important; }
      .h-items col.col-qty { width: 70px !important; }
      .h-items col.col-price { width: 100px !important; }
      .h-items col.col-total { width: 105px !important; }
      .h-items th {
        border: 1px solid #000000 !important;
        background: #f1f5f9 !important;
        color: #000000 !important;
      }
      .h-items th.th-no, .h-items td.no { width: 32px !important; max-width: 32px !important; text-align: center !important; }
      .h-items th.th-name, .h-items td.name { text-align: left !important; }
      .h-items th.th-qty, .h-items td.qty { width: 70px !important; max-width: 70px !important; text-align: center !important; }
      .h-items th.th-price, .h-items td.price { width: 100px !important; max-width: 100px !important; text-align: right !important; }
      .h-items th.th-total, .h-items td.total { width: 105px !important; max-width: 105px !important; text-align: right !important; }
      .h-items td { border: 1px solid #000000 !important; color: #000000 !important; }
    }
  </style>
</head>
<body>
  <div class="wrap">
    <div class="h-header">
      <div class="h-store">
        ${config.printLogo !== false
          ? (logoUrl
              ? `<img src="${esc(logoUrl)}" alt="Logo" class="logo" onerror="this.style.display='none';this.nextElementSibling.style.display='flex';" /><div class="logo-fallback" style="display:none;">${esc(storeName.slice(0,2).toUpperCase())}</div>`
              : `<div class="logo-fallback">${esc(storeName.slice(0,2).toUpperCase())}</div>`
            )
          : ""
        }
        <div class="h-store-info">
          <h1>${esc(storeName)}</h1>
          ${storeAddress ? `<p>${esc(storeAddress)}</p>` : ""}
          ${storePhone ? `<p>Telp: ${esc(storePhone)}</p>` : ""}
        </div>
      </div>
      <div class="h-customer">
        <div class="crow"><span class="clabel">Nomor Retur</span><span class="csep">:</span><span class="cval bold">${esc(returnNumber)}</span></div>
        <div class="crow"><span class="clabel">Referensi Nota</span><span class="csep">:</span><span class="cval bold">${esc(refTx)}</span></div>
        <div class="crow"><span class="clabel">Tanggal Retur</span><span class="csep">:</span><span class="cval">${esc(returnDate)}</span></div>
        <div class="crow"><span class="clabel">Kasir</span><span class="csep">:</span><span class="cval">${esc(cashierName)}</span></div>
        <div class="crow"><span class="clabel">Pelanggan</span><span class="csep">:</span><span class="cval">${esc(customerName)}</span></div>
        <div class="crow"><span class="clabel">Metode Pengembalian</span><span class="csep">:</span><span class="cval">${esc(refundMethod)}</span></div>
        ${reason && reason !== "-" ? `<div class="crow"><span class="clabel">Alasan</span><span class="csep">:</span><span class="cval">${esc(reason)}</span></div>` : ""}
      </div>
    </div>

    <hr class="h-divider" />

    <table class="h-items">
      <colgroup>
        <col class="col-no" style="width: 32px;" />
        <col class="col-name" />
        <col class="col-qty" style="width: 70px;" />
        <col class="col-price" style="width: 100px;" />
        <col class="col-total" style="width: 105px;" />
      </colgroup>
      <thead>
        <tr>
          <th class="th-no">No</th>
          <th class="th-name">Nama Produk</th>
          <th class="th-qty">Jumlah Retur</th>
          <th class="th-price">Harga Satuan</th>
          <th class="th-total">Nilai Pengembalian</th>
        </tr>
      </thead>
      <tbody>
        ${tableRowsHtml}
      </tbody>
    </table>

    <div class="h-footer">
      <div class="left">
        <p class="signature">Hormat Kami</p>
        <p class="thanks">Barang yang diretur telah disesuaikan dengan stok toko.</p>
        <p class="thanks" style="border:none;padding:0;margin-top:2px;">Terima kasih telah bertransaksi.</p>
      </div>
      <div class="right">
        <div class="sum-row strong">
          <span class="label">Total Pengembalian</span>
          <span class="colon">:</span>
          <span class="value">Rp ${fmt(totalRefund)}</span>
        </div>
        <div class="sum-row status">
          <span class="label">Status Retur</span>
          <span class="colon">:</span>
          <span class="value">Berhasil Diproses</span>
        </div>
      </div>
    </div>

    <p class="h-poweredby">Powered by ASAlink</p>

    <div class="actions no-print">
      <button class="btn btn-close" onclick="window.close()">Kembali</button>
      <button class="btn" onclick="window.print()">Cetak</button>
    </div>
  </div>
</body>
</html>`;
  }

  // =========================================================================
  // 2. THERMAL LAYOUT (Vertical 58mm / 80mm)
  // =========================================================================
  const itemRowsHtml = items.map((itm) => {
    const itemName = esc(itm.item_name || itm.name || "Item");
    const qty = itm.quantity || 1;
    const unit = esc(itm.unit_name || "PCS");
    const price = fmt(itm.unit_price || 0);
    const total = fmt(itm.total_price ?? (itm.unit_price * qty));
    return `
      <div class="item-block">
        <div class="item-name">${itemName}</div>
        <div class="item-line">
          <span class="qty">${qty} ${unit} x Rp ${price}</span>
          <span class="total">Rp ${total}</span>
        </div>
      </div>
    `;
  }).join("");

  return `<!DOCTYPE html>
<html>
<head>
  <meta charset="utf-8">
  <title>Bukti Retur Penjualan - ${esc(returnNumber)}</title>
  <style>
    * { box-sizing: border-box; }
    body {
      font-family: 'Courier New', Courier, monospace;
      font-size: ${baseFontSize};
      margin: 0;
      padding: 0;
      background: #f1f5f9;
      color: #000;
      -webkit-print-color-adjust: exact;
      print-color-adjust: exact;
    }
    .wrap {
      width: ${printableWidth};
      max-width: 100%;
      margin: 10px auto;
      background: #fff;
      padding: 6px 4px;
      box-shadow: 0 2px 8px rgba(0,0,0,0.1);
    }
    .center { text-align: center; }
    .bold { font-weight: 700; }
    .store-name {
      text-align: center;
      font-size: 15px;
      font-weight: 700;
      margin: 0;
    }
    .store-sub {
      text-align: center;
      font-size: 10px;
      color: #333;
      margin: 2px 0;
    }
    .div-dash {
      display: block;
      width: 100%;
      overflow: hidden;
      white-space: nowrap;
      color: #333;
      padding: 3px 0;
      margin: 0;
      letter-spacing: 1px;
    }
    .title-box {
      text-align: center;
      font-weight: 700;
      font-size: 13px;
      padding: 2px 0;
    }
    .meta-row {
      display: flex;
      justify-content: space-between;
      gap: 6px;
      font-size: ${baseFontSize};
      line-height: 1.4;
    }
    .item-block {
      margin: 0;
      padding: 2px 0;
      font-size: ${baseFontSize};
      line-height: 1.4;
    }
    .item-name {
      font-weight: 600;
      word-break: break-word;
    }
    .item-line {
      display: flex;
      justify-content: space-between;
      gap: 6px;
      padding-left: 8px;
    }
    .summary-table {
      width: 100%;
      border-collapse: collapse;
      margin: 2px 0;
    }
    .summary-table td {
      padding: 2px 0;
      font-size: ${baseFontSize};
    }
    .summary-table td.val {
      text-align: right;
    }
    .summary-table tr.total-row td {
      font-size: 13px;
      font-weight: 700;
      padding-top: 4px;
    }
    .footer {
      text-align: center;
      margin: 6px 0 0 0;
      font-size: 10px;
      color: #444;
    }
    .footer p { margin: 2px 0; }
    .no-print {
      display: flex;
      justify-content: center;
      gap: 8px;
      margin: 10px 0;
    }
    .btn {
      border: none;
      background: #0f766e;
      color: #fff;
      border-radius: 6px;
      padding: 8px 16px;
      cursor: pointer;
      font-weight: 600;
      font-size: 12px;
    }
    .btn-close { background: #64748b; }
    @page {
      size: ${paper} auto;
      margin: 0;
    }
    @media print {
      body { background: #fff; }
      .wrap {
        width: ${printableWidth} !important;
        max-width: ${printableWidth} !important;
        margin: 0 auto !important;
        padding: 2mm 0 !important;
        box-shadow: none !important;
      }
      .no-print { display: none !important; }
    }
  </style>
</head>
<body>
  <div class="wrap">
    ${logoUrl ? `<div class="center" style="margin-bottom: 4px;"><img src="${esc(logoUrl)}" alt="Logo" style="max-height: 48px; max-width: 140px; object-fit: contain;" /></div>` : ""}
    <div class="store-name">${esc(storeName)}</div>
    ${storeAddress ? `<div class="store-sub">${esc(storeAddress)}</div>` : ""}
    ${storePhone ? `<div class="store-sub">${esc(storePhone)}</div>` : ""}
    <div class="div-dash">${dash}</div>
    <div class="title-box">BUKTI RETUR PENJUALAN</div>
    <div class="div-dash">${dash}</div>

    <div class="meta-row"><span>No. Retur:</span><span class="bold">${esc(returnNumber)}</span></div>
    <div class="meta-row"><span>Ref. Nota:</span><span class="bold">${esc(refTx)}</span></div>
    <div class="meta-row"><span>Tanggal:</span><span>${esc(returnDate)}</span></div>
    <div class="meta-row"><span>Kasir:</span><span>${esc(cashierName)}</span></div>
    <div class="meta-row"><span>Pelanggan:</span><span>${esc(customerName)}</span></div>
    <div class="meta-row"><span>Metode Bayar:</span><span>${esc(refundMethod)}</span></div>
    ${reason && reason !== "-" ? `<div class="meta-row"><span>Alasan:</span><span>${esc(reason)}</span></div>` : ""}

    <div class="div-dash">${dash}</div>
    <div class="bold" style="padding-bottom: 2px;">BARANG DIRETUR:</div>
    ${itemRowsHtml}

    <div class="div-dash">${dash}</div>
    <table class="summary-table">
      <tr class="total-row">
        <td>TOTAL PENGEMBALIAN:</td>
        <td class="val">Rp ${fmt(totalRefund)}</td>
      </tr>
    </table>

    <div class="div-dash">${dash}</div>
    <div class="footer">
      <p>Barang yang diretur telah disesuaikan dengan stok.</p>
      <p style="margin-top: 4px; font-weight: 600;">Terima kasih.</p>
      <p style="font-size: 9px; color: #888; margin-top: 4px;">Powered by ASAlink</p>
    </div>

    <div class="no-print">
      <button class="btn" onclick="window.print()">Cetak Ulang</button>
      <button class="btn btn-close" onclick="window.close()">Tutup</button>
    </div>
  </div>
</body>
</html>`;
}
